import { alertInGrain } from "../utils/grainDialogs.js";
import { ExclamationTriangleIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import React from "react";
import useEveningOrderPool from "../hooks/useEveningOrderPool.js";
import DispatchHeaderActionMenu from "./resultsPageChrome/DispatchHeaderActionMenu.jsx";
import { EveningOrderPoolMenuItem } from "./resultsPageChrome/DispatchHeaderMoreMenu.jsx";
import useMediaQuery, { MOBILE_QUERY } from "../hooks/useMediaQuery.js";
import { Link, useLocation, useNavigate, useNavigation, useRevalidator } from "react-router";
import { readRuntimeRequestProfiles } from "../lib/runtimeRequestProfiling.js";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { invalidatePageDataCacheEntries } from "../hooks/usePageData.js";
import ConfirmationModal from "./ConfirmationModal.jsx";
import OptimizationModeSplitButton from "./OptimizationModeSplitButton.jsx";
import ResultsWorkspace from "./ResultsWorkspace.jsx";
import { RouteMapGroupRangeToggle } from "./RouteMapPanel.jsx";
import DispatchDayPicker from "./resultsPageChrome/DispatchDayPicker.jsx";
import { withDispatchGeotabCheck } from "./resultsPageChrome/DispatchGeotabCheck.jsx";
import { DispatchSnapshotsDropdown } from "./resultsPageChrome/DispatchRibbonSection.jsx";
import {
  ActionButton,
  EmptyState,
  Pill,
  StatusBadge,
  joinClasses,
} from "./ResultsPrimitives.jsx";
import useAccess from "../hooks/useAccess.jsx";
import useDetailViewState from "../hooks/useDetailViewState.js";
import useDispatchPlanningLiveSync from "../hooks/useDispatchPlanningLiveSync.js";
import useOptimizationLaunchAction from "../hooks/useOptimizationLaunchAction.js";
import { useOptimizationJobTracker } from "../hooks/useOptimizationJobTracker.js";
import { useResultsExportTracker } from "../hooks/useResultsExportTracker.jsx";
import { useUserUiPreferences } from "../hooks/useUserUiPreferences.js";
import { ACCESS_PERMISSION_KEYS } from "../lib/accessControl.js";
import { startedTourRestoreChallenge, startedTourRestoreConfirmation } from "../lib/driverAssignmentsApi.js";
import { dispatchAppStatusNotice } from "../utils/appStatusNotice.js";
import {
  applyDispatchPeriodSearchParam,
  buildResultsLatestApiInvalidationUrls,
  normalizeDispatchPeriod,
  readDispatchDateFromSearch,
  readDispatchMonthFromSearch,
  readDispatchPeriodFromSearch,
  writeLastDispatchContext,
} from "../utils/dispatchDateUrls.js";
import { DispatchPeriodToggle } from "./DispatchPeriodToggle.jsx";
import { resolveDispatchDayStatus } from "../utils/dispatchDayControls.js";
import { buildDispatchSourceStaleNotice } from "../utils/resultsChromeData.js";
import { isMissingLatestOptimizationSnapshot, resolveResultsPageState } from "../utils/resultsPageState.js";
import { clearResultsDetailViewSearchParam } from "../utils/resultsDetailViewNavigation.js";
import { resolveSelectedDispatchDayActionState } from "../utils/resultsSelectedDispatchDayActions.js";
import { buildDispatchDayTransitionConfirmationContent } from "../utils/resultsWorkspaceData.js";
import { isValidLastDispatchDate } from "../utils/userUiPreferences.js";
import {
  OPTIMIZATION_RESULTS_REVALIDATION_RETRY_DELAY_MS,
  shouldRetryOptimizationResultsRevalidation,
} from "../utils/optimizationResultsHandoff.js";
const PANEL_SHELL_CLASS_NAME = "relative overflow-hidden rounded-[24px] shadow-panel max-[720px]:rounded-[22px]";
const SOFT_PANEL_SHELL_CLASS_NAME = "relative overflow-hidden rounded-[24px] shadow-none max-[720px]:rounded-[22px]";
const ROUTE_DOCK_SHELL_CLASS_NAME = "relative overflow-hidden rounded-[8px] shadow-none max-[720px]:rounded-[8px]";
const ANALYSIS_PANEL_CLASS_NAME = `${PANEL_SHELL_CLASS_NAME} border border-[rgba(127,135,146,0.12)] bg-white p-[12px] shadow-[0_16px_34px_rgba(15,23,42,0.03)] backdrop-blur-[18px] max-[980px]:p-4`;
const FLEET_DOCK_PANEL_CLASS_NAME = `${ROUTE_DOCK_SHELL_CLASS_NAME} grid max-h-[calc(100vh-32px)] gap-0 border-0 bg-transparent px-0 py-0 [backdrop-filter:none] grid-rows-[minmax(0,1fr)] max-[1280px]:max-h-none max-[980px]:p-0`;
const DISPATCH_MAIN_PANEL_CLASS_NAME = "relative overflow-hidden rounded-[8px] shadow-none max-[720px]:rounded-[8px] grid gap-2 border border-[rgba(127,135,146,0.12)] bg-white p-3 shadow-[0_18px_38px_rgba(15,23,42,0.03)] backdrop-blur-[18px] [&_.pill]:border-2 [&_.pill]:border-[rgba(95,105,118,0.26)] [&_.pill]:font-black [&_[data-status-badge]]:border-2 [&_[data-status-badge]]:border-[rgba(95,105,118,0.26)] [&_[data-status-badge]]:font-black max-[980px]:p-4";
const SUSPENDED_PANEL_CLASS_NAME = `${ANALYSIS_PANEL_CLASS_NAME} grid gap-3.5`;
const DEFAULT_FLEET_DOCK_WIDTH = 264;
const STANDARD_OPTIMIZATION_MODE = "standard";
const MIN_FLEET_DOCK_WIDTH = 264;
const MAX_FLEET_DOCK_WIDTH = 340;
const MIN_RESULTS_MAIN_WIDTH = 620;
const FLEET_DOCK_KEYBOARD_STEP = 16;
const FLEET_DOCK_COLLAPSE_BREAKPOINT = 840;
const ROUTE_CANVAS_OVERLAY_HEIGHT = "clamp(320px, calc(48vh - 60px), 500px)";
const ROUTE_STEPS_DOCK_WIDTH = "clamp(14.75rem, 24vw, 20rem)";
const ROUTE_DOCK_INSET_TOP = 4;
const ROUTE_DOCK_INSET_SIDE = 8;
const ROUTE_DOCK_INSET_BOTTOM = 8;
const RESULTS_DAY_LABEL_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  weekday: "long",
  year: "numeric",
});
const RESULTS_STALE_TIMESTAMP_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  month: "long",
  year: "numeric",
});
const SOURCE_STALE_REASON_LABELS = {
  "data-manager-mutation": "Mutation Data Manager",
  "dispatch-day-closed": "Journée clôturée",
  "dispatch-day-reopened": "Journée rouverte",
  "import-changed-fields": "Import modifié",
  "source-data-updated": "Données source modifiées",
  "source-signature-mismatch": "Sources remplacées",
};
const INITIAL_OPTIMIZE_CONFIRMATION = {
  eyebrow: "Première optimisation",
  title: "Confirmer l'optimisation initiale de la flotte ?",
  description: "Le moteur va produire la première affectation exploitable à partir des fichiers source déjà importés.",
  impactItems: [
    "Les tournées, la carte et les indicateurs de la vue Affectation seront générés dès que le calcul se termine.",
  ],
  summaryItems: [],
  confirmLabel: "Optimiser maintenant",
  cancelLabel: "Annuler",
  tone: "accent",
  warningMessage: "Le calcul peut prendre quelques instants selon le volume des commandes et des camions.",
};

function buildOptimizationAutomationUrl(dispatchDate, dispatchPeriod = "day") {
  const searchParams = new URLSearchParams({
    new: "dispatch.optimize",
    dispatch_date: dispatchDate,
    dispatch_period: normalizeDispatchPeriod(dispatchPeriod),
    optimization_mode: STANDARD_OPTIMIZATION_MODE,
  });
  return `/admin/automations?${searchParams.toString()}`;
}

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function toSafeInteger(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return 0;
  }

  return Math.round(numericValue);
}

function readOptionalFiniteNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function resolveOptimizationDiagnostics(candidate = null) {
  const diagnostics = candidate?.optimization_diagnostics;
  return diagnostics && typeof diagnostics === "object" && !Array.isArray(diagnostics)
    ? diagnostics
    : null;
}

function resolveOptimizationRunId(candidate = null) {
  const diagnostics = resolveOptimizationDiagnostics(candidate);
  return normalizeInlineText(diagnostics?.run_id);
}

function readLatestResultsRequestProfile(minRecordedAtEpochMs = 0) {
  const runtimeProfiles = readRuntimeRequestProfiles();

  for (let index = runtimeProfiles.length - 1; index >= 0; index -= 1) {
    const entry = runtimeProfiles[index];
    if (!entry || entry.path !== "/api/results/latest") {
      continue;
    }

    const recordedAtEpochMs = Number(entry.recordedAtEpochMs);
    if (Number.isFinite(minRecordedAtEpochMs) && minRecordedAtEpochMs > 0 && Number.isFinite(recordedAtEpochMs) && recordedAtEpochMs < minRecordedAtEpochMs) {
      continue;
    }

    return entry;
  }

  return null;
}

function buildOptimizationReferenceProbePayload({
  initialResultsData = null,
  jobState = null,
  latestToFirstPaintMs = null,
} = {}) {
  const currentDiagnostics = resolveOptimizationDiagnostics(initialResultsData);
  const trackedDiagnostics = jobState?.optimizationDiagnostics && typeof jobState.optimizationDiagnostics === "object"
    ? jobState.optimizationDiagnostics
    : null;
  const referenceProbe = jobState?.referenceProbe && typeof jobState.referenceProbe === "object"
    ? jobState.referenceProbe
    : null;

  return {
    backend: currentDiagnostics?.reference_metrics || trackedDiagnostics?.reference_metrics || null,
    backendFullDiagnostics: currentDiagnostics || trackedDiagnostics || null,
    frontend: {
      accepted_at: normalizeInlineText(referenceProbe?.acceptedAt) || null,
      accepted_at_epoch_ms: readOptionalFiniteNumber(referenceProbe?.acceptedAtEpochMs),
      confirm_click_at: normalizeInlineText(referenceProbe?.confirmClickAt) || null,
      confirm_click_at_epoch_ms: readOptionalFiniteNumber(referenceProbe?.confirmClickAtEpochMs),
      preflight_ms: readOptionalFiniteNumber(referenceProbe?.preflightMs),
      post_optimize_roundtrip_ms: readOptionalFiniteNumber(referenceProbe?.postOptimizeRoundtripMs),
      accepted_to_first_poll_ms: readOptionalFiniteNumber(referenceProbe?.acceptedToFirstPollMs),
      poll_count: readOptionalFiniteNumber(referenceProbe?.pollCount) != null ? Math.round(Number(referenceProbe.pollCount)) : 0,
      completed_status_seen_at: normalizeInlineText(referenceProbe?.completedStatusSeenAt) || null,
      completed_status_seen_at_epoch_ms: readOptionalFiniteNumber(referenceProbe?.completedStatusSeenAtEpochMs),
      completed_to_revalidate_ms: readOptionalFiniteNumber(referenceProbe?.completedToRevalidateMs),
      latest_response_recorded_at: normalizeInlineText(referenceProbe?.latestResponseRecordedAt)
        || normalizeInlineText(referenceProbe?.latestRequestProfile?.recordedAt)
        || null,
      latest_response_recorded_at_epoch_ms: readOptionalFiniteNumber(referenceProbe?.latestResponseRecordedAtEpochMs)
        ?? readOptionalFiniteNumber(referenceProbe?.latestRequestProfile?.recordedAtEpochMs),
      results_latest_roundtrip_ms: readOptionalFiniteNumber(referenceProbe?.resultsLatestRoundtripMs),
      latest_to_first_paint_ms: readOptionalFiniteNumber(latestToFirstPaintMs) ?? readOptionalFiniteNumber(referenceProbe?.latestToFirstPaintMs),
    },
    recordedAt: new Date().toISOString(),
    resultsLatestRequestProfile: referenceProbe?.latestRequestProfile || null,
    resultsLatestRuntimeProfile: referenceProbe?.latestRuntimeProfile || null,
    runId: resolveOptimizationRunId(initialResultsData) || normalizeInlineText(trackedDiagnostics?.run_id) || null,
  };
}

function parseDispatchDate(value) {
  const normalizedValue = normalizeInlineText(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizedValue);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsedDate = new Date(year, month - 1, day, 12, 0, 0, 0);

  if (
    Number.isNaN(parsedDate.getTime())
    || parsedDate.getFullYear() !== year
    || parsedDate.getMonth() !== month - 1
    || parsedDate.getDate() !== day
  ) {
    return null;
  }

  return parsedDate;
}

function formatDispatchDayLabel(dispatchDate, fallbackLabel = "") {
  const parsedDate = parseDispatchDate(dispatchDate);
  if (!parsedDate) {
    return normalizeInlineText(fallbackLabel) || normalizeInlineText(dispatchDate) || "Journée de livraison";
  }

  return RESULTS_DAY_LABEL_FORMATTER.format(parsedDate);
}

function formatResultsStaleTimestamp(value) {
  const normalizedValue = normalizeInlineText(value);
  if (!normalizedValue) {
    return "Indisponible";
  }

  const frenchDateTimeMatch = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/.exec(normalizedValue);
  if (frenchDateTimeMatch) {
    // Les libellés `generated_at` « JJ/MM/AAAA HH:MM » sont en UTC (format_snapshot_generated_at_label).
    const parsedDate = new Date(Date.UTC(
      Number(frenchDateTimeMatch[3]),
      Number(frenchDateTimeMatch[2]) - 1,
      Number(frenchDateTimeMatch[1]),
      Number(frenchDateTimeMatch[4] || 12),
      Number(frenchDateTimeMatch[5] || 0),
    ));

    if (!Number.isNaN(parsedDate.getTime())) {
      return RESULTS_STALE_TIMESTAMP_FORMATTER.format(parsedDate);
    }
  }

  const parsedDate = new Date(normalizedValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return RESULTS_STALE_TIMESTAMP_FORMATTER.format(parsedDate);
  }

  return normalizedValue;
}

function resolveSourceStaleReasonLabel(missingLatestOptimization = null) {
  const reasonCandidates = [
    missingLatestOptimization?.sourceDataReason,
    ...(Array.isArray(missingLatestOptimization?.sourceDataReasons) ? missingLatestOptimization.sourceDataReasons : []),
  ].map((value) => normalizeInlineText(value)).filter(Boolean);

  for (const candidate of reasonCandidates) {
    if (SOURCE_STALE_REASON_LABELS[candidate]) {
      return SOURCE_STALE_REASON_LABELS[candidate];
    }
  }

  return "Données source modifiées";
}

function readNormalizedDispatchDayStatus(dispatchDay = null) {
  if (dispatchDay?.status && typeof dispatchDay.status === "object") {
    return dispatchDay.status;
  }

  return resolveDispatchDayStatus(dispatchDay);
}

function resolveDispatchStatusTone(statusKey = "") {
  if (statusKey === "livre" || statusKey === "optimized") {
    return "success";
  }

  if (statusKey === "cloture" || statusKey === "closed") {
    return "danger";
  }

  if (statusKey === "a_revalider" || statusKey === "inconsistent") {
    return "warning";
  }

  return "neutral";
}

function buildDispatchDayMetrics(dispatchDay = null, dispatchPeriod = "day") {
  if (!dispatchDay || typeof dispatchDay !== "object") {
    return [];
  }
  const totalOrdersLabel = normalizeDispatchPeriod(dispatchPeriod) === "evening"
    ? "Commandes du soir"
    : "Commandes du jour";

  return [
    {
      key: "total-orders",
      label: totalOrdersLabel,
      value: toSafeInteger(dispatchDay.order_count),
    },
    {
      key: "active-orders",
      label: "Actives",
      value: toSafeInteger(dispatchDay.active_order_count),
    },
    {
      key: "ready-orders",
      label: "Prêtes",
      value: toSafeInteger(dispatchDay.ready_count),
    },
    {
      key: "assigned-orders",
      label: "Assignées",
      value: toSafeInteger(dispatchDay.assigned_count),
    },
    {
      key: "completed-orders",
      label: "Complétées",
      value: toSafeInteger(dispatchDay.completed_count),
    },
    {
      key: "cancelled-orders",
      label: "Annulées",
      value: toSafeInteger(dispatchDay.cancelled_count),
    },
  ].filter((metric, index) => index < 4 || metric.value > 0);
}

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function parseJsonResponse(response, fallbackMessage) {
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    const message = normalizeInlineText(payload?.error || payload?.detail || payload?.message) || fallbackMessage;
    throw new Error(message);
  }

  return payload;
}

function buildDispatchDateQueryUrl(pathname, dispatchDate, dispatchPeriod = "day") {
  const safeDispatchDate = normalizeInlineText(dispatchDate);
  const searchParams = new URLSearchParams();
  if (safeDispatchDate) {
    searchParams.set("dispatch_date", safeDispatchDate);
  }
  applyDispatchPeriodSearchParam(searchParams, dispatchPeriod);
  const queryString = searchParams.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
}

function buildResultsDispatchUrl(dispatchDate = "", dispatchPeriod = "day") {
  return buildDispatchDateQueryUrl("/results", dispatchDate, dispatchPeriod);
}

function buildEmptyDispatchRestoreSourceChangePreview() {
  return {
    counts: { new: 0, modified_same_date: 0, loading_constraint_changes: 0, date_changed_remove: 0, total: 0 },
    details: { new: [], modified_same_date: [], loading_constraint_changes: [], date_changed_remove: [] },
  };
}

function buildEmptyDispatchRestoreExecutionPreservation() {
  return {
    blocking: false,
    counts: { preservable: 0, blocked: 0, total: 0 },
    details: { preservable: [], blocked: [] },
  };
}

function buildDispatchRestorePreviewFromPayload(payload) {
  const sourceChangePreview = payload?.source_changes && typeof payload.source_changes === "object"
    ? payload.source_changes
    : buildEmptyDispatchRestoreSourceChangePreview();
  return {
    ...sourceChangePreview,
    integrity_conflicts: Array.isArray(payload?.integrity_conflicts)
      ? payload.integrity_conflicts.filter(Boolean)
      : Array.isArray(sourceChangePreview?.details?.integrity_conflicts)
        ? sourceChangePreview.details.integrity_conflicts.filter(Boolean)
        : [],
    address_revalidation: payload?.address_revalidation && typeof payload.address_revalidation === "object"
      ? payload.address_revalidation
      : null,
    execution_preservation: payload?.execution_preservation && typeof payload.execution_preservation === "object"
      ? payload.execution_preservation
      : buildEmptyDispatchRestoreExecutionPreservation(),
    previewLoaded: true,
    related_dispatches: payload?.related_dispatches && typeof payload.related_dispatches === "object"
      ? payload.related_dispatches
      : null,
  };
}

function buildDispatchRestorePreviewError() {
  return {
    ...buildEmptyDispatchRestoreSourceChangePreview(),
    previewError: true,
    previewLoaded: true,
  };
}

async function fetchDispatchSnapshotRestorePreview(snapshotId, dispatchDate, dispatchPeriod = "day") {
  const response = await fetchWithSupabaseAuth(
    buildDispatchDateQueryUrl(`/api/planning/snapshots/${encodeURIComponent(snapshotId)}/restore-preview`, dispatchDate, dispatchPeriod),
  );
  const payload = await parseJsonResponse(response, "Impossible de prévisualiser la restauration.");
  return buildDispatchRestorePreviewFromPayload(payload);
}

function DispatchDayMetricCard({ label = "", value = 0 }) {
  return (
    <div className="grid gap-0.5 bg-white px-3.5 py-2.5">
      <p className="m-0 text-[0.6rem] font-black uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className={joinClasses(["m-0 text-[1.35rem] font-bold tracking-[-0.03em]", value > 0 ? "text-slate-900" : "text-slate-400"])}>{value}</p>
    </div>
  );
}

function MissingSnapshotDispatchRestoreCard({
  snapshotRestore = null,
}) {
  const snapshotItems = Array.isArray(snapshotRestore?.items)
    ? snapshotRestore.items.filter(Boolean)
    : [];
  const errorMessage = normalizeInlineText(snapshotRestore?.errorMessage);
  const isLoading = snapshotRestore?.isLoading === true;
  const canRestore = snapshotRestore?.canRestore === true;
  const restoringSnapshotId = normalizeInlineText(snapshotRestore?.restoringSnapshotId);

  if (!canRestore || isLoading || (!errorMessage && !snapshotItems.length)) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[4px] border border-emerald-200/70 bg-emerald-50/70 px-4 py-3 text-sm text-emerald-950">
      <Pill tone="success">Sauvegarde disponible</Pill>
      <p className="m-0 min-w-[14rem] flex-1 leading-6 text-emerald-900">
        Une sauvegarde dispatch peut être restaurée comme base courante sans lancer une nouvelle optimisation.
      </p>
      {snapshotItems.length ? (
        <DispatchSnapshotsDropdown
          snapshotItems={snapshotItems}
          snapshotInteractions={snapshotRestore}
          disableDeleteBase
          hideDelete
          restoringSnapshotId={restoringSnapshotId}
        />
      ) : null}
      {errorMessage ? (
        <p className="m-0 basis-full text-xs leading-5 text-amber-900">{errorMessage}</p>
      ) : null}
    </div>
  );
}

function ResultsMissingSnapshotShell({
  controls = null,
  interactions = {},
  panelClassName = "",
  showControls = true,
  children = null,
}) {
  return (
    <section className="mt-2.5 grid gap-2 px-4.5 max-[980px]:px-3.5 max-[840px]:px-0">
      {showControls && controls ? <DispatchDayPicker controls={controls} interactions={interactions} showInlineCalendarPreview showStepButtons /> : null}
      <section className={joinClasses([DISPATCH_MAIN_PANEL_CLASS_NAME, panelClassName])}>
        {children}
      </section>
    </section>
  );
}

function ResultsSourceStaleCallout({
  changeCount = 0,
  changeLines = [],
  message = "",
  missingLatestOptimization = null,
  title = "Le dispatch affiché n'inclut pas les derniers changements",
}) {
  if (missingLatestOptimization?.isSourceStale !== true) {
    return null;
  }

  return (
    <div className="grid gap-1 rounded-[4px] border border-l-4 border-amber-200 border-l-amber-600 bg-amber-50 px-3.5 py-2.5 text-[0.8rem] leading-5 text-amber-950">
      <p className="m-0 flex items-center gap-2 text-sm font-bold">
        <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        {title}
      </p>
      <p className="m-0">
        Dernière optimisation <b>{formatResultsStaleTimestamp(missingLatestOptimization.generatedAt)}</b>
        {" · "}dernier changement <b>{formatResultsStaleTimestamp(missingLatestOptimization.sourceDataUpdatedAt)}</b>
        {" "}({resolveSourceStaleReasonLabel(missingLatestOptimization)}).
      </p>
      {message ? <p className="m-0">{message}</p> : null}
      {changeLines.length > 0 ? (
        <details>
          <summary className="w-fit cursor-pointer font-semibold underline">
            Voir {Math.max(changeCount, changeLines.length) > 1 ? `les ${Math.max(changeCount, changeLines.length)} changements` : "le changement"}
          </summary>
          <ul className="m-0 mt-1 grid gap-0.5 pl-4">
            {changeLines.map((line, index) => <li key={`${index}:${line}`}>{line}</li>)}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function ResultsDispatchDaySelectionState({
  apiErrorMessage = "",
  dispatchDayControls = null,
  dispatchDays = [],
  dispatchInteractions = {},
  missingLatestOptimization = null,
  onChooseSuggestedDay = null,
  suggestedDispatchDate = "",
}) {
  const hasDispatchDays = dispatchDays.length > 0;
  const suggestedDispatchLabel = suggestedDispatchDate
    ? formatDispatchDayLabel(suggestedDispatchDate, suggestedDispatchDate)
    : "";
  const selectionFooter = hasDispatchDays ? (
    <>
      <div className="flex flex-wrap gap-2">
        <Pill tone="neutral">{dispatchDays.length} journée{dispatchDays.length > 1 ? "s" : ""} disponible{dispatchDays.length > 1 ? "s" : ""}</Pill>
      </div>

      {suggestedDispatchLabel ? (
        <p className="m-0 text-xs leading-5 text-slate-500">
          Journée proposée par défaut : <span className="font-semibold text-slate-700">{suggestedDispatchLabel}</span>
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2 pt-1">
        {suggestedDispatchDate ? (
          <ActionButton tone="accent" onClick={() => onChooseSuggestedDay?.(suggestedDispatchDate)}>
            Choisir cette journée
          </ActionButton>
        ) : null}
        <ActionButton as={Link} to="/import" tone="ghost">
          Voir les fichiers importés
        </ActionButton>
      </div>
    </>
  ) : null;

  return (
    <EmptyState
      className="gap-3 rounded-[20px] px-5 py-5"
      message={hasDispatchDays
        ? "Des commandes importées sont déjà disponibles. Choisissez d'abord la journée de dispatch à piloter pour afficher son contexte d'affectation, ses mutations et les actions d'optimisation."
        : "Importez des commandes pour faire apparaître des journées de dispatch exploitables dans Affectation."}
      title={hasDispatchDays ? "Choisissez une journée de dispatch" : "Aucune journée de dispatch détectée"}
    >
      <ResultsSourceStaleCallout
        missingLatestOptimization={missingLatestOptimization}
        message="Choisissez une journée puis relancez l'optimisation pour recalculer un dispatch exploitable."
        title="Données modifiées depuis la dernière optimisation"
      />

      {dispatchDayControls ? (
        <DispatchDayPicker
          controls={dispatchDayControls}
          interactions={dispatchInteractions}
          showInlineCalendarPreview
          inlinePreviewFooter={selectionFooter}
        />
      ) : null}

      {!dispatchDayControls && hasDispatchDays ? (
        <div className="flex flex-wrap gap-2">
          <Pill tone="neutral">{dispatchDays.length} journée{dispatchDays.length > 1 ? "s" : ""} disponible{dispatchDays.length > 1 ? "s" : ""}</Pill>
        </div>
      ) : null}

      {!dispatchDayControls && suggestedDispatchLabel ? (
        <p className="m-0 text-xs leading-5 text-slate-500">
          Journée proposée par défaut : <span className="font-semibold text-slate-700">{suggestedDispatchLabel}</span>
        </p>
      ) : null}

      {!hasDispatchDays && apiErrorMessage ? (
        <p className="m-0 text-xs leading-5 text-slate-500">{apiErrorMessage}</p>
      ) : null}

      {!dispatchDayControls ? (
        <div className="flex flex-wrap gap-2 pt-1">
          {suggestedDispatchDate ? (
            <ActionButton tone="accent" onClick={() => onChooseSuggestedDay?.(suggestedDispatchDate)}>
              Choisir cette journée
            </ActionButton>
          ) : null}
          <ActionButton as={Link} to="/import" tone="ghost">
            Voir les fichiers importés
          </ActionButton>
        </div>
      ) : null}
    </EmptyState>
  );
}

function ResultsSelectedDispatchDayState({
  apiErrorMessage = "",
  canCancelOptimization = false,
  canScheduleOptimization = false,
  dispatchDayControls = null,
  dispatchInteractions = {},
  dispatchStatus = null,
  dispatchStatusTone = "neutral",
  missingLatestOptimization = null,
  onOptimizeLaunch = null,
  onScheduleOptimization = null,
  onChangeDispatchPeriod = null,
  isCancelling = false,
  isFinalizing = false,
  isOptimizing = false,
  optimizeFormRef = null,
  onOpenSnapshot = null,
  selectedDispatchDay = null,
  selectedDispatchLabel = "",
  dispatchPeriod = "day",
  dispatchPeriodToggleEnabled = false,
  snapshotRestore = null,
  sourceStaleChangeCount = 0,
  sourceStaleChangeLines = [],
}) {
  const eveningOrderPool = dispatchInteractions.eveningOrderPool;
  const eveningPoolMenu = eveningOrderPool?.visible && dispatchInteractions.canMutatePlanning ? (
    <DispatchHeaderActionMenu label="Plus" contextKey={`${selectedDispatchDay?.dispatch_date}:${dispatchPeriod}`}>
      <EveningOrderPoolMenuItem policy={eveningOrderPool}
        disabled={isOptimizing || ["cloture", "livre"].includes(dispatchStatus?.key)} />
    </DispatchHeaderActionMenu>
  ) : null;
  const metrics = buildDispatchDayMetrics(selectedDispatchDay, dispatchPeriod);
  const statusKey = dispatchStatus?.key || "courant";
  const hasUnavailableSnapshotMessage = normalizeInlineText(apiErrorMessage).length > 0;
  const canRunOptimization = dispatchInteractions.canRunOptimization === true;
  const isClosed = statusKey === "cloture";
  const isDelivered = statusKey === "livre";
  const {
    canCloseDispatchDay,
    canReopenDispatchDay,
    hasSnapshot,
    isFrozen,
    isSourceStale,
    requiresRevalidation,
    showNoSnapshotHint,
    showOpenSnapshotAction,
    showOptimizeAction,
  } = resolveSelectedDispatchDayActionState({
    allowEveningOrderPool: eveningOrderPool?.enabled === true,
    canRunOptimization,
    dispatchDayControls,
    dispatchInteractions,
    dispatchStatus,
    missingLatestOptimization,
    onOpenSnapshot,
    selectedDispatchDay,
  });
  const showStaleSnapshotCallout = isSourceStale && hasSnapshot;
  const headline = formatDispatchDayLabel(selectedDispatchDay?.dispatch_date, selectedDispatchLabel);
  // La carte n'apparaît que si « Optimiser » est permis : jamais sur une journée livrée ou clôturée.
  const dispatchSnapshotRestoreCard = showNoSnapshotHint ? (
    <MissingSnapshotDispatchRestoreCard snapshotRestore={snapshotRestore} />
  ) : null;
  // En Soir, le sélecteur reste visible : sans lui, aucun retour vers Jour.
  const dispatchPeriodToggle = dispatchPeriodToggleEnabled === true || normalizeDispatchPeriod(dispatchPeriod) === "evening" ? (
    <DispatchPeriodToggle
      value={dispatchPeriod}
      onChange={onChangeDispatchPeriod}
    />
  ) : null;
  const orderCount = toSafeInteger(selectedDispatchDay?.order_count);
  const activeOrderCount = toSafeInteger(selectedDispatchDay?.active_order_count);
  const assignedOrderCount = Math.min(toSafeInteger(selectedDispatchDay?.assigned_count), activeOrderCount);
  const assignedPercent = activeOrderCount > 0 ? Math.round((assignedOrderCount / activeOrderCount) * 100) : 0;
  const closedAt = normalizeInlineText(selectedDispatchDay?.closed_at);
  const statusBadge = isClosed
    ? { label: "Fermée", tone: "neutral" }
    : !hasSnapshot
      ? { label: "Non optimisée", tone: "neutral" }
      : { label: dispatchStatus?.label || "Courante", tone: dispatchStatusTone };
  const description = isClosed
    ? `Fermée${closedAt ? ` le ${formatResultsStaleTimestamp(closedAt)}` : ""} · consultation seulement, aucune modification possible.`
    : isDelivered
      ? "Livrée · consultation seulement, aucune modification possible."
      : !hasSnapshot
        ? `${orderCount} commande${orderCount > 1 ? "s" : ""} importée${orderCount > 1 ? "s" : ""}, aucun dispatch calculé pour l'instant.`
        : requiresRevalidation || isSourceStale
          ? "Dispatch calculé, à revalider avant reprise opérationnelle."
          : "Dispatch calculé et consultable.";
  const dayStateAction = canCloseDispatchDay || canReopenDispatchDay ? (
    <ActionButton
      tone="ghost"
      className="ml-auto"
      onClick={() => {
        const dispatchDate = selectedDispatchDay?.dispatch_date;
        if (canCloseDispatchDay) {
          dispatchInteractions.onCloseDispatchDay?.(dispatchDate);
        } else {
          dispatchInteractions.onReopenDispatchDay?.(dispatchDate);
        }
      }}
    >
      {canCloseDispatchDay ? "Clore la journée" : "Rouvrir la journée"}
    </ActionButton>
  ) : null;

  return (
    <div className="grid gap-4 rounded-[4px] border border-[rgba(148,163,184,0.16)] bg-[rgba(248,250,252,0.76)] px-5 py-5">
      <div className={dispatchDayControls ? "grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(24rem,28rem)] xl:items-start xl:gap-5" : "grid"}>
        <div className="grid min-w-0 content-start gap-3.5">
          <div className="grid gap-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="m-0 font-display text-[clamp(1.35rem,2.2vw,1.8rem)] font-semibold tracking-[-0.04em] text-slate-900">{headline}</h2>
              <StatusBadge tone={statusBadge.tone} className="gap-1.5">
                {isClosed ? <LockClosedIcon className="h-3 w-3" aria-hidden="true" /> : null}
                {statusBadge.label}
              </StatusBadge>
              {dispatchPeriodToggle ? <div className="ml-auto">{dispatchPeriodToggle}</div> : null}
            </div>
            <p className="m-0 text-sm leading-6 text-slate-600">{description}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {eveningPoolMenu}
            {showOptimizeAction ? (
              <form
                ref={optimizeFormRef}
                method="post"
                action="/optimize"
                data-loading-overlay-title="Loading page"
                data-loading-overlay-message="Nous préparons les affectations, la carte de routage et les indicateurs de flotte."
              >
                <input type="hidden" name="dispatch_period" value={normalizeDispatchPeriod(dispatchPeriod)} />
                <OptimizationModeSplitButton
                  cancelLabel="Arrêter"
                  cancelLoadingLabel="Annulation demandée..."
                  isCancelling={isCancelling}
                  isFinalizing={isFinalizing}
                  isLoading={isOptimizing}
                  disabled={eveningOrderPool?.isSaving === true}
                  label="Optimiser la flotte"
                  loadingLabel="Optimisation en cours..."
                  onCancel={canCancelOptimization ? onOptimizeLaunch?.cancel : undefined}
                  onLaunch={onOptimizeLaunch?.openConfirm}
                  showCancel={canCancelOptimization}
                />
              </form>
            ) : null}
            {showOpenSnapshotAction ? (
              <ActionButton
                tone={showOptimizeAction ? "ghost" : "accent"}
                onClick={() => {
                  onOpenSnapshot?.(selectedDispatchDay?.dispatch_date);
                }}
              >
                Ouvrir le dispatch
              </ActionButton>
            ) : null}
            {canScheduleOptimization && !isFrozen ? (
              <ActionButton
                tone="ghost"
                onClick={onScheduleOptimization}
              >
                Programmer…
              </ActionButton>
            ) : null}
            {dayStateAction}
          </div>

          <div className="grid gap-px overflow-hidden rounded-[4px] border border-slate-200 bg-slate-200">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-px">
              {metrics.map((metric) => (
                <DispatchDayMetricCard key={metric.key} label={metric.label} value={metric.value} />
              ))}
            </div>
            {activeOrderCount > 0 ? (
              <div className="flex items-center gap-2.5 bg-white px-3.5 py-2">
                <div className="h-1.5 flex-1 bg-slate-200">
                  <div className="h-full bg-accent" style={{ width: `${assignedPercent}%` }} />
                </div>
                <span className="whitespace-nowrap text-xs font-semibold text-slate-600">
                  {assignedOrderCount} / {activeOrderCount} assignée{assignedOrderCount > 1 ? "s" : ""} · {assignedPercent} %
                </span>
              </div>
            ) : null}
          </div>

          {showStaleSnapshotCallout ? (
            <ResultsSourceStaleCallout
              changeCount={sourceStaleChangeCount}
              changeLines={sourceStaleChangeLines}
              missingLatestOptimization={missingLatestOptimization}
              message={isDelivered
                ? "Journée livrée : le Dispatch ne peut plus changer."
                : isFrozen
                  ? "Pour en tenir compte : rouvrez la journée, puis lancez « Optimiser »."
                  : "Relancez Optimiser la flotte pour en tenir compte."}
            />
          ) : null}

          {hasUnavailableSnapshotMessage && !isSourceStale ? (
            <p className="m-0 rounded-[4px] border border-l-4 border-amber-200 border-l-amber-600 bg-amber-50 px-3.5 py-2.5 text-sm leading-6 text-amber-950">{apiErrorMessage}</p>
          ) : null}
          {dispatchSnapshotRestoreCard}
        </div>

        {dispatchDayControls ? (
          <div className="grid min-w-0 content-start gap-3 xl:justify-self-stretch">
            <DispatchDayPicker
              controls={dispatchDayControls}
              interactions={dispatchInteractions}
              inlinePreviewLayout="stack"
              showDayStateActions={false}
              showInlineCalendarPreview
              showStepButtons
              showTriggerStatus={false}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ResultsMissingSnapshotState({
  initialResultsData = null,
  isFirstOptimizationGanttVisible = false,
  onCloseFirstOptimizationGantt = null,
  onOpenFirstOptimizationGantt = null,
  pageState,
}) {
  const optimizeFormRef = React.useRef(null);
  const pendingOptimizationRevalidationRef = React.useRef(false);
  const previousOptimizationStatusRef = React.useRef(null);
  const [dispatchDayConfirmation, setDispatchDayConfirmation] = React.useState(null);
  const [isOptimizeConfirmOpen, setIsOptimizeConfirmOpen] = React.useState(false);
  const [hasScheduledFirstOptimization, setHasScheduledFirstOptimization] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { hasPermission, isReady: isAccessReady } = useAccess();
  const {
    canCancelOptimization,
    isCancelling,
    isFinalizing,
    isOptimizing,
    launchOptimization,
    requestOptimizationCancel,
  } = useOptimizationLaunchAction();
  const { jobState, updateOptimizationReferenceProbe } = useOptimizationJobTracker();
  const selectedDispatchDay = pageState.selectedDispatchDay;
  const selectedDispatchDate = normalizeInlineText(pageState.selectedDispatchDate)
    || normalizeInlineText(selectedDispatchDay?.dispatch_date);
  const dispatchDateFromPayload = typeof initialResultsData?.dispatch_date === "string"
    ? initialResultsData.dispatch_date.trim()
    : "";
  const dispatchPeriodFromSearch = React.useMemo(() => readDispatchPeriodFromSearch(location.search), [location.search]);
  const eveningOrderPool = useEveningOrderPool({
    ...initialResultsData,
    dispatch_date: selectedDispatchDate,
    ui_dispatch_period: dispatchPeriodFromSearch,
  });
  const selectedDispatchStatus = pageState.selectedDispatchStatus
    || pageState.dispatchDayControls?.activeItem?.status
    || pageState.dispatchDayControls?.activeStatus
    || readNormalizedDispatchDayStatus(selectedDispatchDay);
  const selectedDispatchStatusTone = resolveDispatchStatusTone(selectedDispatchStatus?.key);
  const canCloseDispatchDay = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchDayClose);
  const canReopenDispatchDay = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchDayReopen);
  const canRunOptimization = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchRun);
  const canMutatePlanning = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchAssignmentWrite);
  const canScheduleOptimization = canRunOptimization
    && hasPermission(ACCESS_PERMISSION_KEYS.automationsManage)
    && Boolean(selectedDispatchDate);
  const handleScheduleOptimization = React.useCallback(() => {
    if (!selectedDispatchDate) return;
    navigate(buildOptimizationAutomationUrl(selectedDispatchDate, dispatchPeriodFromSearch));
  }, [dispatchPeriodFromSearch, navigate, selectedDispatchDate]);
  const canRestoreDispatchSnapshot = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchSnapshotRestore);
  const [dispatchSnapshotRestoreState, setDispatchSnapshotRestoreState] = React.useState({
    items: [],
    isLoading: false,
    errorMessage: "",
    previewingSnapshotId: "",
    restoringSnapshotId: "",
    restorePreviewById: {},
  });

  const invalidateResultsCache = React.useCallback((dispatchDate = "") => {
    const resultUrl = buildResultsDispatchUrl(dispatchDate, dispatchPeriodFromSearch);

    invalidatePageDataCacheEntries([
      "/api/dashboard",
      "/api/dashboard/home",
      ...buildResultsLatestApiInvalidationUrls({
        currentSearch: location.search,
        resultUrl,
      }),
    ]);
  }, [dispatchPeriodFromSearch, location.search]);

  const refreshDispatchSnapshots = React.useCallback(async () => {
    if (!canRestoreDispatchSnapshot || pageState.kind === "choose-day" || !selectedDispatchDate) {
      setDispatchSnapshotRestoreState({
        items: [],
        isLoading: false,
        errorMessage: "",
        previewingSnapshotId: "",
        restoringSnapshotId: "",
        restorePreviewById: {},
      });
      return;
    }

    setDispatchSnapshotRestoreState((currentState) => ({
      ...currentState,
      errorMessage: "",
      isLoading: true,
    }));

    try {
      const response = await fetchWithSupabaseAuth(
        buildDispatchDateQueryUrl("/api/planning/snapshots", selectedDispatchDate, dispatchPeriodFromSearch),
      );
      const payload = await parseJsonResponse(response, "Impossible de charger les sauvegardes dispatch.");
      setDispatchSnapshotRestoreState((currentState) => ({
        ...currentState,
        errorMessage: "",
        isLoading: false,
        items: Array.isArray(payload?.items) ? payload.items.filter(Boolean) : [],
        restorePreviewById: {},
      }));
    } catch (error) {
      setDispatchSnapshotRestoreState((currentState) => ({
        ...currentState,
        errorMessage: error instanceof Error && error.message
          ? error.message
          : "Impossible de charger les sauvegardes dispatch.",
        isLoading: false,
        items: [],
        restorePreviewById: {},
      }));
    }
  }, [canRestoreDispatchSnapshot, dispatchPeriodFromSearch, pageState.kind, selectedDispatchDate]);

  const previewDispatchSnapshotRestore = React.useCallback(async (snapshotId) => {
    const safeSnapshotId = normalizeInlineText(snapshotId);
    if (!canRestoreDispatchSnapshot || !selectedDispatchDate || !safeSnapshotId) {
      return;
    }

    setDispatchSnapshotRestoreState((currentState) => ({
      ...currentState,
      previewingSnapshotId: safeSnapshotId,
    }));

    try {
      const restorePreview = await fetchDispatchSnapshotRestorePreview(safeSnapshotId, selectedDispatchDate, dispatchPeriodFromSearch);
      setDispatchSnapshotRestoreState((currentState) => ({
        ...currentState,
        previewingSnapshotId: "",
        restorePreviewById: {
          ...(currentState.restorePreviewById || {}),
          [safeSnapshotId]: restorePreview,
        },
      }));
    } catch {
      setDispatchSnapshotRestoreState((currentState) => ({
        ...currentState,
        previewingSnapshotId: "",
        restorePreviewById: {
          ...(currentState.restorePreviewById || {}),
          [safeSnapshotId]: buildDispatchRestorePreviewError(),
        },
      }));
    }
  }, [canRestoreDispatchSnapshot, dispatchPeriodFromSearch, selectedDispatchDate]);

  const navigateWithSearchParams = React.useCallback((mutateSearchParams) => {
    const nextSearchParams = new URLSearchParams(location.search);
    mutateSearchParams(nextSearchParams);
    return navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      },
      { replace: false },
    );
  }, [location.pathname, location.search, navigate]);

  const handleSelectDispatchDay = React.useCallback((dispatchDate) => {
    const safeDispatchDate = normalizeInlineText(dispatchDate);
    if (!safeDispatchDate) {
      return;
    }

    writeLastDispatchContext({
      dispatch_date: safeDispatchDate,
      dispatch_month: safeDispatchDate.slice(0, 7),
      dispatch_period: dispatchPeriodFromSearch,
    });

    navigateWithSearchParams((nextSearchParams) => {
      nextSearchParams.set("dispatch_date", safeDispatchDate);
      nextSearchParams.set("dispatch_month", safeDispatchDate.slice(0, 7));
      applyDispatchPeriodSearchParam(nextSearchParams, dispatchPeriodFromSearch);
      clearResultsDetailViewSearchParam(nextSearchParams);
    });
  }, [dispatchPeriodFromSearch, navigateWithSearchParams]);

  const handleChangeDispatchMonth = React.useCallback((dispatchMonth) => {
    const safeDispatchMonth = normalizeInlineText(dispatchMonth);
    if (!safeDispatchMonth) {
      return;
    }

    writeLastDispatchContext({
      dispatch_date: selectedDispatchDate || dispatchDateFromPayload,
      dispatch_month: safeDispatchMonth,
      dispatch_period: dispatchPeriodFromSearch,
    });

    navigateWithSearchParams((nextSearchParams) => {
      nextSearchParams.set("dispatch_month", safeDispatchMonth);
    });
  }, [dispatchDateFromPayload, dispatchPeriodFromSearch, navigateWithSearchParams, selectedDispatchDate]);

  const handleChangeDispatchPeriod = React.useCallback((dispatchPeriod) => {
    const nextDispatchPeriod = normalizeDispatchPeriod(dispatchPeriod);
    if (nextDispatchPeriod === dispatchPeriodFromSearch) {
      return;
    }

    writeLastDispatchContext({
      dispatch_date: selectedDispatchDate || dispatchDateFromPayload,
      dispatch_month: (selectedDispatchDate || dispatchDateFromPayload || "").slice(0, 7),
      dispatch_period: nextDispatchPeriod,
    });

    navigateWithSearchParams((nextSearchParams) => {
      applyDispatchPeriodSearchParam(nextSearchParams, nextDispatchPeriod);
      const safeDispatchDate = selectedDispatchDate || dispatchDateFromPayload;
      if (safeDispatchDate) {
        nextSearchParams.set("dispatch_date", safeDispatchDate);
        nextSearchParams.set("dispatch_month", safeDispatchDate.slice(0, 7));
      }
    });
  }, [dispatchDateFromPayload, dispatchPeriodFromSearch, navigateWithSearchParams, selectedDispatchDate]);

  const handleToggleDispatchArchive = React.useCallback((isOpen) => {
    navigateWithSearchParams((nextSearchParams) => {
      if (isOpen) {
        nextSearchParams.set("dispatchArchive", "1");
      } else {
        nextSearchParams.delete("dispatchArchive");
      }
    });
  }, [navigateWithSearchParams]);

  const handleOpenSnapshot = React.useCallback((dispatchDate) => {
    const safeDispatchDate = normalizeInlineText(dispatchDate);
    if (!safeDispatchDate) {
      return;
    }

    writeLastDispatchContext({
      dispatch_date: safeDispatchDate,
      dispatch_month: safeDispatchDate.slice(0, 7),
      dispatch_period: dispatchPeriodFromSearch,
    });

    navigateWithSearchParams((nextSearchParams) => {
      nextSearchParams.set("dispatch_date", safeDispatchDate);
      nextSearchParams.set("dispatch_month", safeDispatchDate.slice(0, 7));
      applyDispatchPeriodSearchParam(nextSearchParams, dispatchPeriodFromSearch);
      nextSearchParams.delete("dispatchView");
    });
  }, [dispatchPeriodFromSearch, navigateWithSearchParams]);

  const restoreDispatchSnapshot = React.useCallback(async (snapshotId, { confirmStartedTours = false } = {}) => {
    const safeSnapshotId = normalizeInlineText(snapshotId);
    if (!canRestoreDispatchSnapshot || !selectedDispatchDate || !safeSnapshotId || dispatchSnapshotRestoreState.restoringSnapshotId) {
      return;
    }

    setDispatchSnapshotRestoreState((currentState) => ({
      ...currentState,
      errorMessage: "",
      restoringSnapshotId: safeSnapshotId,
    }));
    dispatchAppStatusNotice({
      eyebrow: "Sauvegarde dispatch",
      message: "Restauration de la sauvegarde en cours.",
      source: "results-dispatch-save-restore",
      status: "running",
      statusLabel: "Traitement en cours",
      title: "Restauration de sauvegarde",
    });

    try {
      let restorePreview = dispatchSnapshotRestoreState.restorePreviewById?.[safeSnapshotId];
      const hasLoadedRestorePreview = restorePreview?.previewLoaded === true || restorePreview?.previewError === true;
      if (!hasLoadedRestorePreview) {
        try {
          restorePreview = await fetchDispatchSnapshotRestorePreview(safeSnapshotId, selectedDispatchDate, dispatchPeriodFromSearch);
        } catch {
          restorePreview = buildDispatchRestorePreviewError();
        }
        const previewForState = restorePreview;
        setDispatchSnapshotRestoreState((currentState) => ({
          ...currentState,
          restorePreviewById: {
            ...(currentState.restorePreviewById || {}),
            [safeSnapshotId]: previewForState,
          },
        }));
      }
      const relatedDispatchesPreview = restorePreview?.related_dispatches && typeof restorePreview.related_dispatches === "object"
        ? restorePreview.related_dispatches
        : null;
      const restoreUrl = buildDispatchDateQueryUrl(
        `/api/planning/snapshots/${encodeURIComponent(safeSnapshotId)}/restore`,
        selectedDispatchDate,
        dispatchPeriodFromSearch,
      );
      const restoreConfirmationParams = [];
      if (relatedDispatchesPreview?.requires_confirmation === true) {
        restoreConfirmationParams.push("confirm_related_dispatches=true");
      }
      if (relatedDispatchesPreview?.requires_removed_end_of_day_load_confirmation === true) {
        restoreConfirmationParams.push("confirm_removed_end_of_day_loads=true");
      }
      if (restorePreview?.address_revalidation?.confirmation_required === true) {
        restoreConfirmationParams.push("confirm_address_revalidation=true");
      }
      if (confirmStartedTours) {
        restoreConfirmationParams.push("confirm_started_tours=true");
      }
      const restoreUrlWithConfirmation = restoreConfirmationParams.length
        ? `${restoreUrl}&${restoreConfirmationParams.join("&")}`
        : restoreUrl;
      const response = await fetchWithSupabaseAuth(
        restoreUrlWithConfirmation,
        {
          headers: {
            Accept: "application/json",
          },
          method: "POST",
        },
      );
      // Tournées commencées dont la sauvegarde change le chauffeur ou le camion : rien n'est écrit tant qu'on ne confirme pas.
      const startedTourChallenge = response.status === 409
        ? startedTourRestoreChallenge(await response.clone().json().catch(() => null), response.status)
        : null;
      if (startedTourChallenge) {
        dispatchAppStatusNotice({
          eyebrow: "Sauvegarde dispatch",
          message: "Confirmez la restauration : elle change le chauffeur ou le camion d'une tournée commencée.",
          source: "results-dispatch-save-restore",
          status: "failed",
          statusLabel: "Action requise",
          title: "Restauration à confirmer",
        });
        const dismiss = () => setDispatchDayConfirmation(null);
        setDispatchDayConfirmation({
          ...startedTourRestoreConfirmation(startedTourChallenge),
          isOpen: true,
          onCancel: dismiss,
          onClose: dismiss,
          onConfirm: () => {
            dismiss();
            void restoreDispatchSnapshot(safeSnapshotId, { confirmStartedTours: true });
          },
        });
        return;
      }
      const payload = await parseJsonResponse(response, "Impossible de restaurer la sauvegarde dispatch.");

      const restoredRelatedDispatches = payload?.related_dispatches && typeof payload.related_dispatches === "object"
        ? payload.related_dispatches
        : relatedDispatchesPreview;
      const affectedDispatchDates = Array.isArray(restoredRelatedDispatches?.affected_dispatch_dates)
        ? restoredRelatedDispatches.affected_dispatch_dates.map(normalizeInlineText).filter(Boolean)
        : [];
      const cacheInvalidationDates = new Set([selectedDispatchDate, ...affectedDispatchDates]);
      cacheInvalidationDates.forEach((dispatchDateValue) => invalidateResultsCache(dispatchDateValue));
      writeLastDispatchContext({
        dispatch_date: selectedDispatchDate,
        dispatch_month: selectedDispatchDate.slice(0, 7),
        dispatch_period: dispatchPeriodFromSearch,
      });
      navigateWithSearchParams((nextSearchParams) => {
        nextSearchParams.set("dispatch_date", selectedDispatchDate);
        nextSearchParams.set("dispatch_month", selectedDispatchDate.slice(0, 7));
        applyDispatchPeriodSearchParam(nextSearchParams, dispatchPeriodFromSearch);
        nextSearchParams.delete("dispatchView");
      });
      revalidator.revalidate();

      dispatchAppStatusNotice({
        eyebrow: "Sauvegarde dispatch",
        message: normalizeInlineText(payload?.message) || "La sauvegarde sélectionnée est redevenue le dispatch courant.",
        source: "results-dispatch-save-restore",
        status: "completed",
        statusLabel: "Prêt",
        title: "Sauvegarde restaurée",
      });
    } catch (error) {
      dispatchAppStatusNotice({
        eyebrow: "Sauvegarde dispatch",
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible de restaurer la sauvegarde dispatch.",
        source: "results-dispatch-save-restore",
        status: "failed",
        statusLabel: "Action requise",
        title: "Restauration interrompue",
      });
    } finally {
      setDispatchSnapshotRestoreState((currentState) => ({
        ...currentState,
        restoringSnapshotId: "",
      }));
    }
  }, [
    canRestoreDispatchSnapshot,
    dispatchSnapshotRestoreState.restoringSnapshotId,
    dispatchSnapshotRestoreState.restorePreviewById,
    dispatchPeriodFromSearch,
    invalidateResultsCache,
    navigateWithSearchParams,
    revalidator,
    selectedDispatchDate,
  ]);

  const runDispatchDayMutation = React.useCallback(async (dispatchDate, action) => {
    const safeDispatchDate = normalizeInlineText(dispatchDate);
    if (!safeDispatchDate) {
      return;
    }

    const isClosing = action === "close";
    dispatchAppStatusNotice({
      eyebrow: "Journée dispatch",
      message: isClosing
        ? "Fermeture en cours. Les mutations manuelles seront bloquées dès validation."
        : "Réouverture en cours. Les mutations manuelles seront de nouveau autorisées une fois validées.",
      source: "results-dispatch-day",
      status: "running",
      statusLabel: "Traitement en cours",
      title: isClosing ? "Fermeture de journée en cours" : "Réouverture de journée en cours",
    });

    try {
      const response = await fetchWithSupabaseAuth(`/api/dispatch-days/${encodeURIComponent(safeDispatchDate)}/${action}`, {
        headers: {
          Accept: "application/json",
        },
        method: "POST",
      });
      const payload = await parseJsonSafely(response);

      if (!response.ok) {
        throw new Error(
          normalizeInlineText(payload?.error)
          || normalizeInlineText(payload?.detail)
          || (isClosing ? "Impossible de fermer la journée de dispatch." : "Impossible de rouvrir la journée de dispatch."),
        );
      }

      invalidateResultsCache(safeDispatchDate);
      await revalidator.revalidate();
      dispatchAppStatusNotice({
        eyebrow: "Journée dispatch",
        message: isClosing
          ? `La journée ${safeDispatchDate} est fermée et reste visible dans l'historique.`
          : `La journée ${safeDispatchDate} accepte de nouveau les mutations manuelles.`,
        source: "results-dispatch-day",
        status: "completed",
        statusLabel: "Prêt",
        title: isClosing ? "Journée dispatch fermée" : "Journée dispatch réouverte",
      });
    } catch (error) {
      dispatchAppStatusNotice({
        eyebrow: "Journée dispatch",
        message: error instanceof Error && error.message
          ? error.message
          : (isClosing ? "Impossible de fermer la journée de dispatch." : "Impossible de rouvrir la journée de dispatch."),
        source: "results-dispatch-day",
        status: "failed",
        statusLabel: "Action requise",
        title: isClosing ? "Clôture de la journée interrompue" : "Réouverture de la journée interrompue",
      });
    }
  }, [invalidateResultsCache, revalidator]);

  const dismissDispatchDayConfirmation = React.useCallback(() => {
    setDispatchDayConfirmation(null);
  }, []);

  const openDispatchDayConfirmation = React.useCallback((dispatchDate, action) => {
    const safeDispatchDate = normalizeInlineText(dispatchDate);
    if (!safeDispatchDate) {
      return;
    }

    const dispatchDayRecord = (Array.isArray(pageState.dispatchDays) ? pageState.dispatchDays : []).find(
      (item) => normalizeInlineText(item?.dispatch_date) === safeDispatchDate,
    ) || { dispatch_date: safeDispatchDate };
    const transition = buildDispatchDayTransitionConfirmationContent(dispatchDayRecord, action);
    const confirm = () => {
      dismissDispatchDayConfirmation();
      void runDispatchDayMutation(safeDispatchDate, action);
    };
    // Écarts Geotab : la fenêtre Geotab de la journée tient lieu de confirmation.
    const content = action === "close"
      ? withDispatchGeotabCheck(transition, safeDispatchDate, { confirm, cancel: dismissDispatchDayConfirmation })
      : transition;
    if (!content) {
      return;
    }

    setDispatchDayConfirmation({
      ...content,
      isOpen: true,
      onCancel: dismissDispatchDayConfirmation,
      onClose: dismissDispatchDayConfirmation,
      onConfirm: confirm,
    });
  }, [dismissDispatchDayConfirmation, pageState.dispatchDays, runDispatchDayMutation]);

  const dispatchInteractions = React.useMemo(() => ({
    eveningOrderPool,
    canMutatePlanning,
    canCloseDispatchDay,
    canReopenDispatchDay,
    canRunOptimization,
    onCloseDispatchDay(dispatchDate) {
      openDispatchDayConfirmation(dispatchDate, "close");
    },
    onChangeDispatchMonth: handleChangeDispatchMonth,
    onReopenDispatchDay(dispatchDate) {
      openDispatchDayConfirmation(dispatchDate, "reopen");
    },
    onSelectDispatchDay: handleSelectDispatchDay,
    onToggleDispatchArchive: handleToggleDispatchArchive,
  }), [
    eveningOrderPool,
    canMutatePlanning,
    canCloseDispatchDay,
    canReopenDispatchDay,
    canRunOptimization,
    handleChangeDispatchMonth,
    handleSelectDispatchDay,
    handleToggleDispatchArchive,
    openDispatchDayConfirmation,
  ]);

  React.useEffect(() => {
    void refreshDispatchSnapshots();
  }, [refreshDispatchSnapshots]);

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshDispatchSnapshots();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refreshDispatchSnapshots]);

  const dispatchSnapshotRestoreInteractions = React.useMemo(() => ({
    ...dispatchSnapshotRestoreState,
    canRestore: canRestoreDispatchSnapshot,
    onPreviewRestore: canRestoreDispatchSnapshot ? previewDispatchSnapshotRestore : undefined,
    onRefresh: canRestoreDispatchSnapshot ? refreshDispatchSnapshots : undefined,
    onRestore: canRestoreDispatchSnapshot ? restoreDispatchSnapshot : undefined,
  }), [
    canRestoreDispatchSnapshot,
    dispatchSnapshotRestoreState,
    previewDispatchSnapshotRestore,
    refreshDispatchSnapshots,
    restoreDispatchSnapshot,
  ]);

  React.useEffect(() => {
    const previousStatus = previousOptimizationStatusRef.current;
    previousOptimizationStatusRef.current = jobState.status;

    // « idle » : le serveur ne connaît plus le calcul suivi ; son résultat a pu être enregistré.
    const didCommitResults = jobState.status === "completed"
      || jobState.status === "idle"
      || (jobState.status === "cancelled" && jobState.resultCommitted === true);
    if (["running", "cancelling"].includes(previousStatus) && didCommitResults) {
      pendingOptimizationRevalidationRef.current = true;
    }

    if (!pendingOptimizationRevalidationRef.current || revalidator.state !== "idle") {
      return;
    }

    pendingOptimizationRevalidationRef.current = false;
    const completedStatusSeenAtEpochMs = readOptionalFiniteNumber(jobState.referenceProbe?.completedStatusSeenAtEpochMs);
    if (completedStatusSeenAtEpochMs != null) {
      updateOptimizationReferenceProbe({
        completedToRevalidateMs: Math.max(Date.now() - completedStatusSeenAtEpochMs, 0),
      });
    }
    invalidateResultsCache(selectedDispatchDate);
    revalidator.revalidate();
  }, [invalidateResultsCache, jobState.referenceProbe?.completedStatusSeenAtEpochMs, jobState.resultCommitted, jobState.status, revalidator, revalidator.state, selectedDispatchDate, updateOptimizationReferenceProbe]);

  React.useEffect(() => {
    const trackedDispatchDate = normalizeInlineText(jobState?.dispatchDate);
    const trackedDispatchPeriod = normalizeDispatchPeriod(jobState?.dispatchPeriod);
    const hasMatchingActiveJob = pageState.kind === "selected-day-no-snapshot"
      && ["running", "cancelling"].includes(jobState?.status)
      && Boolean(selectedDispatchDate)
      && trackedDispatchDate === selectedDispatchDate
      && trackedDispatchPeriod === dispatchPeriodFromSearch;
    if (!hasMatchingActiveJob) {
      return;
    }

    setHasScheduledFirstOptimization(true);
    onOpenFirstOptimizationGantt?.({
      dispatchDate: selectedDispatchDate,
      dispatchPeriod: dispatchPeriodFromSearch,
      orderCount: toSafeInteger(selectedDispatchDay?.active_order_count)
        || toSafeInteger(selectedDispatchDay?.order_count),
    });
  }, [
    dispatchPeriodFromSearch,
    jobState?.dispatchDate,
    jobState?.dispatchPeriod,
    jobState?.status,
    onOpenFirstOptimizationGantt,
    pageState.kind,
    selectedDispatchDate,
    selectedDispatchDay?.active_order_count,
    selectedDispatchDay?.order_count,
  ]);

  React.useEffect(() => {
    if (!isFirstOptimizationGanttVisible || !hasScheduledFirstOptimization || isOptimizing) {
      return;
    }

    const shouldRestoreMissingSnapshot = jobState?.status === "failed"
      || jobState?.status === "idle"
      || (jobState?.status === "cancelled" && jobState?.resultCommitted !== true);
    if (!shouldRestoreMissingSnapshot) {
      return;
    }

    setHasScheduledFirstOptimization(false);
    onCloseFirstOptimizationGantt?.();
  }, [
    hasScheduledFirstOptimization,
    isFirstOptimizationGanttVisible,
    isOptimizing,
    jobState?.resultCommitted,
    jobState?.status,
    onCloseFirstOptimizationGantt,
  ]);

  const dismissOptimizeConfirmation = React.useCallback(() => {
    setIsOptimizeConfirmOpen(false);
    if (!isOptimizing) {
      setHasScheduledFirstOptimization(false);
      onCloseFirstOptimizationGantt?.();
    }
  }, [isOptimizing, onCloseFirstOptimizationGantt]);

  const openOptimizeConfirmation = React.useCallback(() => {
    if (pageState.kind === "selected-day-no-snapshot" && selectedDispatchDate) {
      setHasScheduledFirstOptimization(false);
      onOpenFirstOptimizationGantt?.({
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: dispatchPeriodFromSearch,
        orderCount: toSafeInteger(selectedDispatchDay?.active_order_count)
          || toSafeInteger(selectedDispatchDay?.order_count),
      });
    }
    setIsOptimizeConfirmOpen(true);
  }, [
    dispatchPeriodFromSearch,
    onOpenFirstOptimizationGantt,
    pageState.kind,
    selectedDispatchDate,
    selectedDispatchDay?.active_order_count,
    selectedDispatchDay?.order_count,
  ]);

  const handleOptimizeConfirm = () => {
    const form = optimizeFormRef.current;
    if (!(form instanceof HTMLFormElement) || isOptimizing || !selectedDispatchDay) {
      return;
    }

    setIsOptimizeConfirmOpen(false);
    const didScheduleOptimization = launchOptimization({
      form,
      optimizationMode: STANDARD_OPTIMIZATION_MODE,
      preflight: {
        allowEveningOrderPool: eveningOrderPool.enabled,
        totalOrders: toSafeInteger(selectedDispatchDay.active_order_count) || toSafeInteger(selectedDispatchDay.order_count),
        totalTrackedFleet: Number.isFinite(Number(initialResultsData?.active_truck_count))
          ? Number(initialResultsData.active_truck_count)
          : null,
      },
      schedule(runLaunch) {
        window.requestAnimationFrame(() => {
          void runLaunch();
        });
      },
    });
    if (!didScheduleOptimization) {
      setHasScheduledFirstOptimization(false);
      onCloseFirstOptimizationGantt?.();
      return;
    }

    setHasScheduledFirstOptimization(true);
  };

  const sourceStaleNotice = React.useMemo(
    () => buildDispatchSourceStaleNotice(initialResultsData || {}),
    [initialResultsData],
  );

  const handleOptimizationCancel = async () => {
    try {
      await requestOptimizationCancel();
    } catch (error) {
      await alertInGrain(error instanceof Error && error.message
        ? error.message
        : "Impossible d'arrêter l'optimisation en cours.");
    }
  };

  return (
    <ResultsMissingSnapshotShell
      controls={pageState.dispatchDayControls}
      interactions={dispatchInteractions}
      panelClassName={pageState.kind === "choose-day" ? "overflow-visible" : ""}
      showControls={false}
    >
      {pageState.kind === "choose-day" ? (
        <ResultsDispatchDaySelectionState
          apiErrorMessage={pageState.apiErrorMessage}
          dispatchDayControls={pageState.dispatchDayControls}
          dispatchDays={pageState.dispatchDays}
          dispatchInteractions={dispatchInteractions}
          missingLatestOptimization={pageState.missingLatestOptimization}
          onChooseSuggestedDay={handleSelectDispatchDay}
          suggestedDispatchDate={pageState.suggestedDispatchDate}
        />
      ) : (
        <ResultsSelectedDispatchDayState
          apiErrorMessage={pageState.apiErrorMessage}
          canCancelOptimization={canCancelOptimization}
          canScheduleOptimization={canScheduleOptimization}
          dispatchDayControls={pageState.dispatchDayControls}
          dispatchInteractions={dispatchInteractions}
          dispatchPeriod={dispatchPeriodFromSearch}
          dispatchPeriodToggleEnabled={initialResultsData?.dispatch_period_toggle_enabled === true}
          dispatchStatus={selectedDispatchStatus}
          dispatchStatusTone={selectedDispatchStatusTone}
          isCancelling={isCancelling}
          isFinalizing={isFinalizing}
          isOptimizing={isOptimizing}
          missingLatestOptimization={pageState.missingLatestOptimization}
          onOptimizeLaunch={{
            cancel: handleOptimizationCancel,
            openConfirm: openOptimizeConfirmation,
          }}
          onScheduleOptimization={handleScheduleOptimization}
          onChangeDispatchPeriod={handleChangeDispatchPeriod}
          onOpenSnapshot={handleOpenSnapshot}
          optimizeFormRef={optimizeFormRef}
          selectedDispatchDay={selectedDispatchDay}
          selectedDispatchLabel={pageState.dispatchDayControls?.selectedLabel || selectedDispatchDay?.label || selectedDispatchDate}
          snapshotRestore={dispatchSnapshotRestoreInteractions}
          sourceStaleChangeCount={sourceStaleNotice?.invalidationEventCount}
          sourceStaleChangeLines={sourceStaleNotice?.causeLines}
        />
      )}

      <ConfirmationModal
        {...INITIAL_OPTIMIZE_CONFIRMATION}
        isOpen={isOptimizeConfirmOpen}
        onCancel={dismissOptimizeConfirmation}
        onClose={dismissOptimizeConfirmation}
        onConfirm={handleOptimizeConfirm}
      />

      {dispatchDayConfirmation ? <ConfirmationModal {...dispatchDayConfirmation} /> : null}
    </ResultsMissingSnapshotShell>
  );
}

function resolveIsFleetDockResizable() {
  if (typeof window === "undefined") {
    return true;
  }

  return window.innerWidth > FLEET_DOCK_COLLAPSE_BREAKPOINT;
}

function clampFleetDockWidth(width, containerWidth = 0) {
  const normalizedWidth = Number.isFinite(width) ? width : DEFAULT_FLEET_DOCK_WIDTH;

  if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
    return Math.max(MIN_FLEET_DOCK_WIDTH, Math.min(MAX_FLEET_DOCK_WIDTH, normalizedWidth));
  }

  const maxAllowedWidth = Math.max(
    MIN_FLEET_DOCK_WIDTH,
    Math.min(
      MAX_FLEET_DOCK_WIDTH,
      containerWidth - MIN_RESULTS_MAIN_WIDTH,
    ),
  );

  return Math.max(MIN_FLEET_DOCK_WIDTH, Math.min(maxAllowedWidth, normalizedWidth));
}

export default function ResultsPage({ initialResultsData = null }) {
  const { jobState, updateOptimizationReferenceProbe } = useOptimizationJobTracker();
  const {
    lastDispatchDate,
    setLastDispatchDate,
    status: userUiPreferencesStatus,
    userId: userUiPreferencesUserId,
  } = useUserUiPreferences();
  const optimizationReferencePaintRef = React.useRef("");
  const optimizationResultsRetryRef = React.useRef({
    attemptCount: 0,
    jobId: "",
    timeoutId: null,
  });
  const [firstOptimizationGanttScope, setFirstOptimizationGanttScope] = React.useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const dispatchDateFromPayload = typeof initialResultsData?.dispatch_date === "string"
    ? initialResultsData.dispatch_date.trim()
    : "";
  const isMissingSnapshotPayload = isMissingLatestOptimizationSnapshot(initialResultsData);
  const pageState = React.useMemo(() => resolveResultsPageState({
    initialResultsData,
    locationSearch: location.search,
  }), [initialResultsData, location.search]);
  const dispatchDateFromUrl = React.useMemo(
    () => readDispatchDateFromSearch(location.search),
    [location.search],
  );
  const availableDispatchDates = React.useMemo(() => new Set(
    (Array.isArray(initialResultsData?.dispatch_days) ? initialResultsData.dispatch_days : [])
      .map((dispatchDay) => normalizeInlineText(dispatchDay?.dispatch_date))
      .filter((dispatchDate) => isValidLastDispatchDate(dispatchDate) && dispatchDate),
  ), [initialResultsData?.dispatch_days]);
  const openedDispatchDate = React.useMemo(() => {
    if (dispatchDateFromUrl) {
      return isValidLastDispatchDate(dispatchDateFromUrl)
        && availableDispatchDates.has(dispatchDateFromUrl)
        ? dispatchDateFromUrl
        : "";
    }

    const candidates = isMissingSnapshotPayload
      ? [
        dispatchDateFromPayload,
        normalizeInlineText(initialResultsData?.default_dispatch_date),
      ]
      : [dispatchDateFromPayload];
    return candidates.find((dispatchDate) => (
      isValidLastDispatchDate(dispatchDate)
      && dispatchDate
      && availableDispatchDates.has(dispatchDate)
    )) || "";
  }, [
    availableDispatchDates,
    dispatchDateFromPayload,
    dispatchDateFromUrl,
    initialResultsData?.default_dispatch_date,
    isMissingSnapshotPayload,
  ]);
  const dispatchPeriodFromSearch = React.useMemo(() => readDispatchPeriodFromSearch(location.search), [location.search]);
  const selectedDispatchDate = normalizeInlineText(
    pageState.selectedDispatchDate
    || dispatchDateFromUrl
    || dispatchDateFromPayload,
  );
  const isFirstOptimizationGanttVisible = pageState.kind === "selected-day-no-snapshot"
    && firstOptimizationGanttScope?.dispatchDate === selectedDispatchDate
    && firstOptimizationGanttScope?.dispatchPeriod === dispatchPeriodFromSearch;
  const openFirstOptimizationGantt = React.useCallback(({
    dispatchDate = "",
    dispatchPeriod = "day",
    orderCount = 0,
  } = {}) => {
    const normalizedDispatchDate = normalizeInlineText(dispatchDate);
    if (!normalizedDispatchDate) {
      return;
    }

    setFirstOptimizationGanttScope({
      dispatchDate: normalizedDispatchDate,
      dispatchPeriod: normalizeDispatchPeriod(dispatchPeriod),
      orderCount: toSafeInteger(orderCount),
    });
  }, []);
  const closeFirstOptimizationGantt = React.useCallback(() => {
    setFirstOptimizationGanttScope(null);
  }, []);

  React.useEffect(() => {
    const retryState = optimizationResultsRetryRef.current;
    const trackedJobId = normalizeInlineText(jobState?.jobId);
    const trackedRunId = normalizeInlineText(jobState?.optimizationDiagnostics?.run_id);
    const authoritativeRunId = resolveOptimizationRunId(initialResultsData);

    if (retryState.jobId !== trackedJobId) {
      if (retryState.timeoutId !== null && typeof window !== "undefined") {
        window.clearTimeout(retryState.timeoutId);
      }
      retryState.attemptCount = 0;
      retryState.jobId = trackedJobId;
      retryState.timeoutId = null;
    }

    if (authoritativeRunId && authoritativeRunId === trackedRunId) {
      if (retryState.timeoutId !== null && typeof window !== "undefined") {
        window.clearTimeout(retryState.timeoutId);
      }
      retryState.attemptCount = 0;
      retryState.timeoutId = null;
      return undefined;
    }

    if (
      typeof window === "undefined"
      || retryState.timeoutId !== null
      || !shouldRetryOptimizationResultsRevalidation({
        currentDispatchDate: selectedDispatchDate,
        currentDispatchPeriod: dispatchPeriodFromSearch,
        jobDispatchDate: jobState?.dispatchDate,
        jobDispatchPeriod: jobState?.dispatchPeriod,
        attemptCount: retryState.attemptCount,
        authoritativeRunId,
        resultCommitted: jobState?.resultCommitted === true,
        revalidatorState: revalidator.state,
        status: jobState?.status,
        trackedRunId,
      })
    ) {
      return undefined;
    }

    retryState.timeoutId = window.setTimeout(() => {
      retryState.timeoutId = null;
      retryState.attemptCount += 1;
      invalidatePageDataCacheEntries(buildResultsLatestApiInvalidationUrls({
        currentSearch: location.search,
        resultUrl: jobState?.resultUrl,
      }));
      revalidator.revalidate();
    }, OPTIMIZATION_RESULTS_REVALIDATION_RETRY_DELAY_MS);

    return () => {
      if (retryState.timeoutId !== null) {
        window.clearTimeout(retryState.timeoutId);
        retryState.timeoutId = null;
      }
    };
  }, [
    initialResultsData,
    jobState?.jobId,
    jobState?.dispatchDate,
    jobState?.dispatchPeriod,
    selectedDispatchDate,
    dispatchPeriodFromSearch,
    jobState?.optimizationDiagnostics?.run_id,
    jobState?.resultCommitted,
    jobState?.resultUrl,
    jobState?.status,
    location.search,
    revalidator,
    revalidator.state,
  ]);

  React.useEffect(() => {
    if (!firstOptimizationGanttScope) {
      return;
    }

    const scopeStillMatches = pageState.kind === "selected-day-no-snapshot"
      && firstOptimizationGanttScope.dispatchDate === selectedDispatchDate
      && firstOptimizationGanttScope.dispatchPeriod === dispatchPeriodFromSearch;
    if (!scopeStillMatches) {
      setFirstOptimizationGanttScope(null);
    }
  }, [dispatchPeriodFromSearch, firstOptimizationGanttScope, pageState.kind, selectedDispatchDate]);

  React.useEffect(() => {
    const dispatchMonthFromUrl = readDispatchMonthFromSearch(location.search);
    const dispatchPeriodFromUrl = readDispatchPeriodFromSearch(location.search);

    if (openedDispatchDate) {
      writeLastDispatchContext({
        dispatch_date: openedDispatchDate,
        dispatch_month: dispatchMonthFromUrl || openedDispatchDate.slice(0, 7),
        dispatch_period: dispatchPeriodFromUrl,
      });
      return;
    }

    if (dispatchMonthFromUrl || dispatchPeriodFromUrl === "evening") {
      writeLastDispatchContext({
        dispatch_month: dispatchMonthFromUrl,
        dispatch_period: dispatchPeriodFromUrl,
      });
    }
  }, [location.search, openedDispatchDate]);

  React.useEffect(() => {
    if (
      !openedDispatchDate
      || !userUiPreferencesUserId
      || userUiPreferencesStatus === "idle"
      || userUiPreferencesStatus === "loading"
      || openedDispatchDate === lastDispatchDate
    ) {
      return;
    }

    void setLastDispatchDate(openedDispatchDate, { immediate: true }).catch(() => {});
  }, [
    lastDispatchDate,
    openedDispatchDate,
    setLastDispatchDate,
    userUiPreferencesStatus,
    userUiPreferencesUserId,
  ]);

  React.useEffect(() => {
    if (!isMissingSnapshotPayload || dispatchDateFromUrl || !openedDispatchDate) {
      return;
    }

    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set("dispatch_date", openedDispatchDate);
    nextSearchParams.set("dispatch_month", openedDispatchDate.slice(0, 7));
    applyDispatchPeriodSearchParam(nextSearchParams, dispatchPeriodFromSearch);
    navigate(
      {
        pathname: location.pathname,
        search: `?${nextSearchParams.toString()}`,
      },
      { replace: true },
    );
  }, [
    dispatchDateFromUrl,
    dispatchPeriodFromSearch,
    isMissingSnapshotPayload,
    location.pathname,
    location.search,
    navigate,
    openedDispatchDate,
  ]);

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    document.body.classList.toggle("results-evening-session", dispatchPeriodFromSearch === "evening");
    return () => {
      document.body.classList.remove("results-evening-session");
    };
  }, [dispatchPeriodFromSearch]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const currentRunId = resolveOptimizationRunId(initialResultsData);
    const trackedRunId = normalizeInlineText(jobState?.optimizationDiagnostics?.run_id);
    if (!currentRunId || !trackedRunId || currentRunId !== trackedRunId) {
      return undefined;
    }

    const completedSeenAtEpochMs = readOptionalFiniteNumber(jobState?.referenceProbe?.completedStatusSeenAtEpochMs);
    const latestRequestProfile = readLatestResultsRequestProfile(completedSeenAtEpochMs);
    if (latestRequestProfile && (
      jobState?.referenceProbe?.latestResponseRecordedAtEpochMs == null
      || jobState?.referenceProbe?.resultsLatestRoundtripMs == null
    )) {
      updateOptimizationReferenceProbe({
        latestRequestProfile,
        latestResponseRecordedAt: latestRequestProfile.recordedAt || null,
        latestResponseRecordedAtEpochMs: latestRequestProfile.recordedAtEpochMs ?? null,
        latestRuntimeProfile: latestRequestProfile.runtimeProfile || null,
        resultsLatestRoundtripMs: latestRequestProfile.durationMs ?? null,
      });
      return undefined;
    }

    const latestResponseRecordedAtEpochMs = readOptionalFiniteNumber(jobState?.referenceProbe?.latestResponseRecordedAtEpochMs);
    if (latestResponseRecordedAtEpochMs == null) {
      return undefined;
    }

    const paintKey = `${currentRunId}:${latestResponseRecordedAtEpochMs}`;
    if (optimizationReferencePaintRef.current === paintKey) {
      return undefined;
    }

    let frameHandle = 0;
    frameHandle = window.requestAnimationFrame(() => {
      optimizationReferencePaintRef.current = paintKey;
      const latestToFirstPaintMs = Math.max(Date.now() - latestResponseRecordedAtEpochMs, 0);
      updateOptimizationReferenceProbe({ latestToFirstPaintMs });
      window.__grainOptimizationReferenceProbe = buildOptimizationReferenceProbePayload({
        initialResultsData,
        jobState,
        latestToFirstPaintMs,
      });
    });

    return () => {
      if (frameHandle) {
        window.cancelAnimationFrame(frameHandle);
      }
    };
  }, [initialResultsData, jobState, updateOptimizationReferenceProbe]);

  const shouldRenderLoadedPage = pageState.kind === "loaded" || isFirstOptimizationGanttVisible;

  return (
    <>
      {pageState.kind !== "loaded" ? (
        <div
          className={isFirstOptimizationGanttVisible ? "hidden" : "contents"}
          data-results-missing-snapshot-controller="true"
        >
          <ResultsMissingSnapshotState
            initialResultsData={initialResultsData}
            isFirstOptimizationGanttVisible={isFirstOptimizationGanttVisible}
            onCloseFirstOptimizationGantt={closeFirstOptimizationGantt}
            onOpenFirstOptimizationGantt={openFirstOptimizationGantt}
            pageState={pageState}
          />
        </div>
      ) : null}

      {shouldRenderLoadedPage ? (
        <ResultsLoadedPage
          firstOptimizationOrderCount={firstOptimizationGanttScope?.orderCount || 0}
          forceFleetView={Boolean(firstOptimizationGanttScope)}
          initialResultsData={initialResultsData}
          key="results-loaded-page"
          manageOptimizationRevalidation={pageState.kind === "loaded"}
        />
      ) : null}
    </>
  );
}

function ResultsLoadedPage({
  firstOptimizationOrderCount = 0,
  forceFleetView = false,
  initialResultsData = null,
  manageOptimizationRevalidation = true,
}) {
  const mobile = useMediaQuery(MOBILE_QUERY);
  const resultsGridRef = React.useRef(null);
  const resultsBoardRef = React.useRef(null);
  const routeMapRootRef = React.useRef(null);
  const previousOptimizationStatusRef = React.useRef(null);
  const pendingOptimizationRevalidationRef = React.useRef(false);
  const [fleetDockWidth, setFleetDockWidth] = React.useState(DEFAULT_FLEET_DOCK_WIDTH);
  const [hasCustomFleetDockWidth, setHasCustomFleetDockWidth] = React.useState(false);
  const [resultsLayoutWidth, setResultsLayoutWidth] = React.useState(0);
  const [activeResizePointerId, setActiveResizePointerId] = React.useState(null);
  const [isFleetDockResizable, setIsFleetDockResizable] = React.useState(resolveIsFleetDockResizable);
  const [routeCanvasOverlayFrame, setRouteCanvasOverlayFrame] = React.useState(null);
  const routeKpiBandRef = React.useRef(null);
  const location = useLocation();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { jobState, updateOptimizationReferenceProbe } = useOptimizationJobTracker();
  const { downloadReadyResultsExport, exportState, syncResultsExportFingerprint } = useResultsExportTracker();
  const detailViewState = useDetailViewState({
    forcedViewName: forceFleetView ? "fleet" : "",
    initialResultsData,
  });
  const dispatchDateFromPayload = typeof initialResultsData?.dispatch_date === "string"
    ? initialResultsData.dispatch_date.trim()
    : "";
  const isMissingSnapshotPayload = isMissingLatestOptimizationSnapshot(initialResultsData);
  const dispatchMonthFromSearch = React.useMemo(() => readDispatchMonthFromSearch(location.search), [location.search]);
  const dispatchPeriodFromSearch = React.useMemo(() => readDispatchPeriodFromSearch(location.search), [location.search]);
  const invalidateResultsCache = React.useCallback((dispatchDate = "") => {
    const resultUrl = buildResultsDispatchUrl(dispatchDate, dispatchPeriodFromSearch);

    invalidatePageDataCacheEntries([
      "/api/dashboard",
      "/api/dashboard/home",
      ...buildResultsLatestApiInvalidationUrls({
        currentSearch: location.search,
        resultUrl,
      }),
    ]);
  }, [dispatchPeriodFromSearch, location.search]);
  const handleChangeDispatchPeriod = React.useCallback((dispatchPeriod) => {
    const nextDispatchPeriod = normalizeDispatchPeriod(dispatchPeriod);
    if (nextDispatchPeriod === dispatchPeriodFromSearch) {
      return;
    }

    const safeDispatchDate = dispatchDateFromPayload || readDispatchDateFromSearch(location.search);
    writeLastDispatchContext({
      dispatch_date: safeDispatchDate,
      dispatch_month: safeDispatchDate ? safeDispatchDate.slice(0, 7) : dispatchMonthFromSearch,
      dispatch_period: nextDispatchPeriod,
    });

    const nextSearchParams = new URLSearchParams(location.search);
    applyDispatchPeriodSearchParam(nextSearchParams, nextDispatchPeriod);
    if (safeDispatchDate) {
      nextSearchParams.set("dispatch_date", safeDispatchDate);
      nextSearchParams.set("dispatch_month", safeDispatchDate.slice(0, 7));
    }
    navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      },
      { replace: false },
    );
  }, [dispatchDateFromPayload, dispatchMonthFromSearch, dispatchPeriodFromSearch, location.pathname, location.search, navigate]);
  const isDispatchArchiveOpen = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("dispatchArchive") === "1";
  }, [location.search]);
  const hydratedResultsData = React.useMemo(() => {
    if (!initialResultsData || typeof initialResultsData !== "object") {
      return initialResultsData;
    }

    return {
      ...initialResultsData,
      ...(forceFleetView ? {
        ui_first_optimization_order_count: toSafeInteger(firstOptimizationOrderCount),
        ui_first_optimization_waiting: true,
      } : {}),
      ui_dispatch_archive_open: isDispatchArchiveOpen,
      ui_dispatch_month: dispatchMonthFromSearch,
      ui_dispatch_period: dispatchPeriodFromSearch,
    };
  }, [dispatchMonthFromSearch, dispatchPeriodFromSearch, firstOptimizationOrderCount, forceFleetView, initialResultsData, isDispatchArchiveOpen]);
  const hasActiveDetailView = Boolean(detailViewState.activeView);
  const isFleetDetailView = detailViewState.activeView === "fleet";
  const loadedPageState = React.useMemo(() => resolveResultsPageState({
    initialResultsData,
    locationSearch: location.search,
  }), [initialResultsData, location.search]);
  const currentResultsExportFingerprint = React.useMemo(() => {
    const exportContext = initialResultsData?.export_context && typeof initialResultsData.export_context === "object"
      ? initialResultsData.export_context
      : {};
    const snapshotGeneratedAt = typeof exportContext?.generated_at === "string" ? exportContext.generated_at.trim() : "";
    const planningSummary = initialResultsData?.planning_summary && typeof initialResultsData.planning_summary === "object"
      ? initialResultsData.planning_summary
      : {};
    const planningUpdatedAt = typeof planningSummary?.updated_at === "string" ? planningSummary.updated_at.trim() : "";
    return `${snapshotGeneratedAt}::${planningUpdatedAt}`;
  }, [initialResultsData]);
  const defaultFleetDockWidth = React.useMemo(
    () => clampFleetDockWidth(DEFAULT_FLEET_DOCK_WIDTH, resultsLayoutWidth),
    [resultsLayoutWidth],
  );

  const maxFleetDockWidth = clampFleetDockWidth(MAX_FLEET_DOCK_WIDTH, resultsLayoutWidth);

  const updateFleetDockWidthFromClientX = (clientX) => {
    const gridElement = resultsGridRef.current;
    if (!(gridElement instanceof HTMLElement)) {
      return;
    }

    const nextWidth = clampFleetDockWidth(
      clientX - gridElement.getBoundingClientRect().left,
      resultsLayoutWidth || gridElement.getBoundingClientRect().width,
    );

    setHasCustomFleetDockWidth(true);
    setFleetDockWidth((currentWidth) => {
      if (Math.abs(currentWidth - nextWidth) < 1) {
        return currentWidth;
      }

      return nextWidth;
    });
  };

  React.useEffect(() => {
    if (hasCustomFleetDockWidth) {
      return;
    }

    setFleetDockWidth(defaultFleetDockWidth);
  }, [defaultFleetDockWidth, hasCustomFleetDockWidth]);

  React.useEffect(() => {
    const gridElement = resultsGridRef.current;
    if (!(gridElement instanceof HTMLElement)) {
      return undefined;
    }

    const syncLayoutMetrics = () => {
      const nextLayoutWidth = gridElement.getBoundingClientRect().width;
      setResultsLayoutWidth(nextLayoutWidth);
      setIsFleetDockResizable(resolveIsFleetDockResizable());
      setFleetDockWidth((currentWidth) => clampFleetDockWidth(currentWidth, nextLayoutWidth));
    };

    syncLayoutMetrics();

    if (typeof window === "undefined") {
      return undefined;
    }

    window.addEventListener("resize", syncLayoutMetrics);

    if (typeof ResizeObserver === "undefined") {
      return () => {
        window.removeEventListener("resize", syncLayoutMetrics);
      };
    }

    const observer = new ResizeObserver(() => {
      syncLayoutMetrics();
    });

    observer.observe(gridElement);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncLayoutMetrics);
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const routeMapRootElement = routeMapRootRef.current;
    if (!(routeMapRootElement instanceof HTMLElement) || hasActiveDetailView) {
      setRouteCanvasOverlayFrame(null);
      return undefined;
    }

    let frameHandle = 0;
    let resizeObserver = null;

    const syncRouteCanvasOverlayFrame = () => {
      frameHandle = 0;

      if (window.innerWidth <= FLEET_DOCK_COLLAPSE_BREAKPOINT) {
        setRouteCanvasOverlayFrame(null);
        return;
      }

      const mapShellElement = routeMapRootElement.querySelector(".dispatch-map-shell");
      const measuredElement = mapShellElement instanceof HTMLElement ? mapShellElement : routeMapRootElement;
      const measuredRect = measuredElement.getBoundingClientRect();

      if (measuredRect.width <= 0 || measuredRect.height <= 0) {
        setRouteCanvasOverlayFrame(null);
        return;
      }

      const containingElement = resultsBoardRef.current;
      const containingRect = containingElement instanceof HTMLElement
        ? containingElement.getBoundingClientRect()
        : null;

      const nextFrame = {
        height: Math.round(measuredRect.height),
        left: containingRect ? Math.round(measuredRect.left - containingRect.left) : Math.round(measuredRect.left),
        rightOffset: containingRect
          ? Math.max(Math.round(containingRect.right - measuredRect.right), 0)
          : Math.max(Math.round(window.innerWidth - measuredRect.right), 0),
        top: containingRect ? Math.round(measuredRect.top - containingRect.top) : Math.round(measuredRect.top),
      };

      setRouteCanvasOverlayFrame((currentFrame) => {
        if (
          currentFrame
          && currentFrame.height === nextFrame.height
          && currentFrame.left === nextFrame.left
          && currentFrame.rightOffset === nextFrame.rightOffset
          && currentFrame.top === nextFrame.top
        ) {
          return currentFrame;
        }

        return nextFrame;
      });
    };

    const scheduleRouteCanvasOverlayFrameSync = () => {
      if (frameHandle) {
        return;
      }

      frameHandle = window.requestAnimationFrame(syncRouteCanvasOverlayFrame);
    };

    scheduleRouteCanvasOverlayFrameSync();
    window.addEventListener("resize", scheduleRouteCanvasOverlayFrameSync);
    window.addEventListener("scroll", scheduleRouteCanvasOverlayFrameSync, { passive: true });

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        scheduleRouteCanvasOverlayFrameSync();
      });
      resizeObserver.observe(routeMapRootElement);
      // La bande d'indicateurs au-dessus déplace la carte quand elle se remplit.
      if (routeKpiBandRef.current instanceof HTMLElement) {
        resizeObserver.observe(routeKpiBandRef.current);
      }

      const mapShellElement = routeMapRootElement.querySelector(".dispatch-map-shell");
      if (mapShellElement instanceof HTMLElement) {
        resizeObserver.observe(mapShellElement);
      }
    }

    return () => {
      if (frameHandle) {
        window.cancelAnimationFrame(frameHandle);
      }

      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleRouteCanvasOverlayFrameSync);
      window.removeEventListener("scroll", scheduleRouteCanvasOverlayFrameSync);
    };
  }, [hasActiveDetailView]);

  React.useEffect(() => {
    if (activeResizePointerId == null || !isFleetDockResizable || typeof document === "undefined") {
      return undefined;
    }

    const handlePointerMove = (event) => {
      if (event.pointerId !== activeResizePointerId) {
        return;
      }

      updateFleetDockWidthFromClientX(event.clientX);
    };

    const handlePointerUp = (event) => {
      if (event.pointerId !== activeResizePointerId) {
        return;
      }

      setActiveResizePointerId(null);
    };

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [activeResizePointerId, isFleetDockResizable, resultsLayoutWidth]);

  React.useEffect(() => {
    const previousStatus = previousOptimizationStatusRef.current;
    previousOptimizationStatusRef.current = jobState.status;

    if (!manageOptimizationRevalidation) {
      pendingOptimizationRevalidationRef.current = false;
      return;
    }

    // « idle » : le serveur ne connaît plus le calcul suivi ; son résultat a pu être enregistré.
    const didCommitResults = jobState.status === "completed"
      || jobState.status === "idle"
      || (jobState.status === "cancelled" && jobState.resultCommitted === true);
    if (["running", "cancelling"].includes(previousStatus) && didCommitResults) {
      pendingOptimizationRevalidationRef.current = true;
    }

    if (!pendingOptimizationRevalidationRef.current || revalidator.state !== "idle") {
      return;
    }

    pendingOptimizationRevalidationRef.current = false;
    const completedStatusSeenAtEpochMs = readOptionalFiniteNumber(jobState.referenceProbe?.completedStatusSeenAtEpochMs);
    if (completedStatusSeenAtEpochMs != null) {
      updateOptimizationReferenceProbe({
        completedToRevalidateMs: Math.max(Date.now() - completedStatusSeenAtEpochMs, 0),
      });
    }
    revalidator.revalidate();
  }, [jobState.referenceProbe?.completedStatusSeenAtEpochMs, jobState.resultCommitted, jobState.status, manageOptimizationRevalidation, revalidator, revalidator.state, updateOptimizationReferenceProbe]);

  useDispatchPlanningLiveSync({
    currentResultsData: initialResultsData,
    dispatchDate: dispatchDateFromPayload,
    dispatchPeriod: dispatchPeriodFromSearch,
    enabled: Boolean(dispatchDateFromPayload) && !isMissingSnapshotPayload && navigation.state === "idle",
    invalidateResultsCache,
    revalidator,
  });

  React.useEffect(() => {
    syncResultsExportFingerprint(currentResultsExportFingerprint);
  }, [currentResultsExportFingerprint, syncResultsExportFingerprint]);

  React.useEffect(() => {
    if (!dispatchDateFromPayload) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const currentDispatchDate = typeof searchParams.get("dispatch_date") === "string"
      ? searchParams.get("dispatch_date").trim()
      : "";

    if (currentDispatchDate === dispatchDateFromPayload || (!currentDispatchDate && isMissingSnapshotPayload)) {
      return;
    }

    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set("dispatch_date", dispatchDateFromPayload);
    navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      },
      { replace: true },
    );
  }, [dispatchDateFromPayload, isMissingSnapshotPayload, location.pathname, location.search, navigate]);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (
      searchParams.get("downloadExport") !== "1"
      || exportState.status !== "ready_async"
      || (currentResultsExportFingerprint && exportState.fingerprint !== currentResultsExportFingerprint)
    ) {
      return undefined;
    }

    let isDisposed = false;

    const runDownload = async () => {
      await downloadReadyResultsExport();
      if (isDisposed) {
        return;
      }

      const nextSearchParams = new URLSearchParams(location.search);
      nextSearchParams.delete("downloadExport");
      navigate(
        {
          pathname: location.pathname,
          search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
        },
        { replace: true },
      );
    };

    void runDownload();

    return () => {
      isDisposed = true;
    };
  }, [currentResultsExportFingerprint, downloadReadyResultsExport, exportState.fingerprint, exportState.status, location.pathname, location.search, navigate]);

  const handleFleetDockResizeStart = (event) => {
    if (!isFleetDockResizable) {
      return;
    }

    event.preventDefault();
    updateFleetDockWidthFromClientX(event.clientX);
    setActiveResizePointerId(event.pointerId);
  };

  const handleFleetDockResizeKeyDown = (event) => {
    if (!isFleetDockResizable) {
      return;
    }

    let nextWidth = fleetDockWidth;

    if (event.key === "ArrowLeft") {
      nextWidth -= FLEET_DOCK_KEYBOARD_STEP;
    } else if (event.key === "ArrowRight") {
      nextWidth += FLEET_DOCK_KEYBOARD_STEP;
    } else if (event.key === "Home") {
      nextWidth = MIN_FLEET_DOCK_WIDTH;
    } else if (event.key === "End") {
      nextWidth = maxFleetDockWidth;
    } else {
      return;
    }

    event.preventDefault();
    setHasCustomFleetDockWidth(true);
    setFleetDockWidth(clampFleetDockWidth(nextWidth, resultsLayoutWidth));
  };

  const isStandardRouteCanvasLayout = !hasActiveDetailView;
  const resultsGridStyle = { gridTemplateColumns: "minmax(0,1fr)" };
  const routeCanvasOverlayHeight = routeCanvasOverlayFrame
    ? `${routeCanvasOverlayFrame.height}px`
    : ROUTE_CANVAS_OVERLAY_HEIGHT;
  const fleetDockTopInset = ROUTE_DOCK_INSET_TOP;
  const fleetDockSideInset = ROUTE_DOCK_INSET_SIDE;
  const fleetDockBottomInset = ROUTE_DOCK_INSET_BOTTOM;
  const fleetDockInsetHeight = routeCanvasOverlayFrame
    ? `${Math.max(routeCanvasOverlayFrame.height - (fleetDockTopInset + fleetDockBottomInset), 320)}px`
    : `calc(${ROUTE_CANVAS_OVERLAY_HEIGHT} - ${fleetDockTopInset + fleetDockBottomInset}px)`;
  const fleetDockOverlayStyle = {
    "--fleet-dock-width": `${Math.round(fleetDockWidth)}px`,
    "--route-canvas-overlay-height": fleetDockInsetHeight,
    ...(routeCanvasOverlayFrame
      ? {
          left: `${routeCanvasOverlayFrame.left + fleetDockSideInset}px`,
          position: "fixed",
          top: `${routeCanvasOverlayFrame.top + fleetDockTopInset}px`,
          zIndex: 1400,
        }
      : null),
  };
  const routeStepsOverlayStyle = {
    "--route-canvas-overlay-height": fleetDockInsetHeight,
    "--route-steps-dock-width": ROUTE_STEPS_DOCK_WIDTH,
    ...(routeCanvasOverlayFrame
      ? {
          position: "fixed",
          right: `${routeCanvasOverlayFrame.rightOffset + fleetDockSideInset}px`,
          top: `${routeCanvasOverlayFrame.top + fleetDockTopInset}px`,
          zIndex: 1400,
        }
      : null),
  };

  return (
    <div className="contents" data-dispatch-context-surface="true">
      <div className={mobile && (!detailViewState.activeView || isFleetDetailView) ? "hidden" : "contents"}>
      <section className="block sticky top-[57px] min-[1281px]:top-0 z-20 shrink-0 bg-surface-results shadow-[0_-0.5rem_var(--results-shell-main)]">
        <section
          className="dispatch-ribbon relative z-20 overflow-visible rounded-none border-0 bg-surface-results p-0 pb-0.5 shadow-none [backdrop-filter:none]"
          data-results-dispatch-ribbon-root="true"
        />
        <div className="mt-2.5">
          <div
            className="col-span-full mx-auto flex w-full max-w-[1800px] min-w-0 flex-wrap items-center gap-3 px-3.25 pt-px pb-1 max-[980px]:items-stretch"
            data-results-detail-toolbar-root="true"
          >
            <div className="min-w-0 shrink-0" data-results-detail-view-tabs-root="true" />
            <div className="min-w-0 flex-1" aria-hidden="true" />
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 max-[980px]:w-full max-[980px]:justify-end">
              <div data-results-detail-view-actions-root="true" />
            </div>
          </div>
        </div>
      </section>

      <div className="contents" data-results-alert-banner-root="true" />

      <section
        ref={resultsGridRef}
        className="mt-0.5 grid min-w-0 items-start gap-x-2 gap-y-1.5"
        style={resultsGridStyle}
      >
        <div className="min-w-0 self-start">
          <section ref={resultsBoardRef} id="results-board" className={`${DISPATCH_MAIN_PANEL_CLASS_NAME} ${isFleetDetailView ? "rounded-[4px]! pt-0! min-[981px]:px-1 min-[981px]:py-2" : "pt-2!"}`}>
            <div className="grid gap-1.25" data-results-standard-view-root="true">
              <div className="contents" data-results-route-header-root="true" />

              {/* Indicateurs de la tournée au-dessus de la carte : la carte reste entièrement visible. */}
              <div ref={routeKpiBandRef} className="flex min-w-0 flex-wrap items-center gap-1.5 min-[841px]:flex-nowrap">
                <div className="dispatch-kpi-grid min-w-0 flex-1" data-results-kpi-grid-root="true" />
                <RouteMapGroupRangeToggle className="shrink-0" />
              </div>

              <div
                className={joinClasses([
                  "grid min-w-0 items-start gap-1.25 grid-cols-[minmax(0,1fr)] max-[840px]:grid-cols-1",
                  isStandardRouteCanvasLayout ? "min-[841px]:relative" : "",
                ])}
                data-results-stage-grid
              >
                {!hasActiveDetailView ? (
                  <div
                    className="min-w-0 self-start min-[841px]:pointer-events-none min-[841px]:absolute min-[841px]:left-0 min-[841px]:top-0 min-[841px]:z-20 min-[841px]:h-(--route-canvas-overlay-height) min-[841px]:w-(--fleet-dock-width)"
                    style={fleetDockOverlayStyle}
                  >
                    <aside className="min-w-0 min-[841px]:pointer-events-auto min-[841px]:relative min-[841px]:h-full min-[841px]:w-full">
                      <section className={joinClasses([FLEET_DOCK_PANEL_CLASS_NAME, "min-[841px]:h-full"])}>
                        <div className="h-full min-h-0" data-results-fleet-column-root="true" />
                      </section>
                    </aside>
                  </div>
                ) : null}

                <div ref={routeMapRootRef} className="grid min-w-0 gap-1.25" data-results-route-map-root="true" />

                {!hasActiveDetailView ? (
                  <div
                    className="min-w-0 min-[841px]:pointer-events-none min-[841px]:absolute min-[841px]:right-0 min-[841px]:top-0 min-[841px]:z-20 min-[841px]:h-(--route-canvas-overlay-height) min-[841px]:w-(--route-steps-dock-width)"
                    style={routeStepsOverlayStyle}
                  >
                    <aside className="min-w-0 min-[841px]:pointer-events-auto min-[841px]:relative min-[841px]:h-full min-[841px]:w-full">
                      <div className="h-full min-h-0" data-results-route-steps-root="true" />
                    </aside>
                  </div>
                ) : null}
              </div>

              <div className="grid items-start gap-1" data-results-support-panels-root="true" />
            </div>

            <section id="analysis-stage" className={`hidden gap-3 ${isFleetDetailView ? "" : "pt-1.5"}`} data-results-analysis-stage-root="true">
              <section
                id="fleet-view"
                className="hidden scroll-mt-5.5"
                data-detail-panel="fleet"
                role="region"
                aria-label="Vue flotte"
              >
                <div data-results-fleet-view-root="true" />
              </section>

              <section
                id="suspended-center"
                className={`${SUSPENDED_PANEL_CLASS_NAME} hidden scroll-mt-5.5`}
                data-detail-panel="suspended"
                role="region"
                aria-label="Commandes suspendues"
              >
                <div data-results-suspended-panel-root="true" />
              </section>

              <section
                id="warnings-center"
                className={`${ANALYSIS_PANEL_CLASS_NAME} hidden scroll-mt-5.5`}
                data-detail-panel="warnings"
                role="region"
                aria-label="Avertissements"
              >
                <div data-results-warnings-center-root="true" />
              </section>

              <section
                id="errors-center"
                className={`${ANALYSIS_PANEL_CLASS_NAME} hidden scroll-mt-5.5`}
                data-detail-panel="errors"
                role="region"
                aria-label="Alertes"
              >
                <div data-results-errors-center-root="true" />
              </section>
            </section>
          </section>
        </div>
      </section>

      </div>
      <div className="contents" data-results-workspace-root="true">
        <ResultsWorkspace
          detailViewState={detailViewState}
          initialResultsData={hydratedResultsData}
        />
      </div>
    </div>
  );
}
